import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Card, Steps, Button, Form, Input, Select, Radio, Tabs,
  DatePicker, message, Progress, Row, Col, Divider,
  Tag, Space, Modal
} from 'antd';
import {
  RocketOutlined, SettingOutlined, PictureOutlined,
  CheckCircleOutlined, SyncOutlined
} from '@ant-design/icons';
import api, { getImageUrl } from '../utils/api';
import moment from 'moment';

const { Step } = Steps;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;

const CREATIVE_COPY_PAGE_SIZE = 50;
const MATERIAL_COLLECTION_PAGE_SIZE = 50;

const mergeListById = (prev, items, idKey = 'id') => {
  const seen = new Set((prev || []).map((item) => item[idKey]));
  const merged = [...(prev || [])];
  (items || []).forEach((item) => {
    if (!seen.has(item[idKey])) {
      merged.push(item);
      seen.add(item[idKey]);
    }
  });
  return merged;
};

// 辅助函数：将对象格式的 locales 转换为 labelInValue 格式
const normalizeLocales = (locales) => {
  if (!locales || !Array.isArray(locales) || locales.length === 0) return [];
  
  // 如果是对象数组 [{id, name}]，转换为 labelInValue 格式
  if (typeof locales[0] === 'object' && locales[0] !== null) {
    return locales.map(l => ({
      value: String(l.id),
      label: l.name || String(l.id)
    }));
  }
  
  // 字符串或数字数组，转换为 labelInValue 格式
  const idToName = {
    'zh': '中文',
    'en': '英语',
    'ja': '日语',
    'ko': '韩语',
    'fr': '法语',
    'de': '德语',
    'es': '西班牙语',
    '48': '中文（简体）',
    '49': '中文（繁体）',
    '24': '英语（英国）',
    '6': '英语（美国）',
    '40': '日语',
    '56': '韩语',
    '16': '法语',
    '8': '德语',
    '1': '西班牙语',
  };
  return locales.map(l => ({
    value: String(l),
    label: idToName[String(l)] || String(l)
  }));
};

const CREATION_MODES = {
  BUILD_PLAN: 'build_plan',
  CBO_REGULAR_BID: 'cbo_regular_bid',
  ABO_REGULAR_BID: 'abo_regular_bid',
};

const SERIES_MODES = {
  REGULAR: 'regular',
  ROI: 'roi',
};

const getCreationModeLabel = (mode) => {
  const labels = {
    build_plan: '搭建计划',
    cbo_regular_bid: 'CBO 常规出价',
    abo_regular_bid: 'ABO 常规出价',
  };
  return labels[mode] || '搭建计划';
};

const getSeriesModeLabel = (mode) => {
  const labels = {
    regular: '常规模式（一刀/出价）',
    roi: 'ROI系列模式',
  };
  return labels[mode] || labels.regular;
};

const getEffectiveSeriesMode = (template, seriesMode) =>
  template?.series_mode || seriesMode || SERIES_MODES.REGULAR;

const applyRoiAdsetDefaults = (adset, isRoi) => (
  isRoi
    ? {
        ...adset,
        optimization_goal: 'VALUE',
        performance_goal: 'MAXIMIZE_CONVERSION_VALUE',
        billing_event: 'IMPRESSIONS',
        bid_amount: undefined,
        bid_strategy: undefined,
      }
    : adset
);

const normalizeMetaBidStrategy = (value, fallback = 'LOWEST_COST_WITHOUT_CAP') => {
  const aliases = {
    BID_CAP: 'LOWEST_COST_WITH_BID_CAP',
    MIN_ROAS: 'LOWEST_COST_WITH_MIN_ROAS',
    LOWEST_COST: 'LOWEST_COST_WITHOUT_CAP',
    HIGHEST_VOLUME_OR_VALUE: 'LOWEST_COST_WITHOUT_CAP',
  };
  const v = (value || '').toUpperCase();
  const valid = new Set([
    'LOWEST_COST_WITHOUT_CAP',
    'LOWEST_COST_WITH_BID_CAP',
    'COST_CAP',
    'LOWEST_COST_WITH_MIN_ROAS',
  ]);
  if (valid.has(v)) return v;
  return aliases[v] || fallback;
};

// CBO 常规出价 — 与 Facebook 广告管理工具一致的系列竞价策略
const CBO_REGULAR_BID_STRATEGIES = [
  { value: 'LOWEST_COST_WITHOUT_CAP', label: '最高数量或价值' },
  { value: 'COST_CAP', label: '单次成效费用目标' },
  { value: 'LOWEST_COST_WITH_BID_CAP', label: '竞价上限' },
];

const ROI_CAMPAIGN_BID_STRATEGIES = [
  { value: 'LOWEST_COST_WITH_MIN_ROAS', label: '广告花费回报目标' },
];

const getCampaignBidStrategiesForSeries = (seriesMode) => (
  seriesMode === SERIES_MODES.ROI
    ? ROI_CAMPAIGN_BID_STRATEGIES
    : CBO_REGULAR_BID_STRATEGIES
);

const buildCampaignParamsForMode = ({
  creationMode,
  seriesMode,
  budgetMode,
  campaignData,
  selectedTemplate,
}) => {
  const params = {
    name: campaignData.name,
    objective: seriesMode === SERIES_MODES.ROI
      ? (['OUTCOME_SALES', 'OUTCOME_APP_PROMOTION'].includes(campaignData.objective)
        ? campaignData.objective
        : 'OUTCOME_SALES')
      : campaignData.objective,
    status: 'ACTIVE',
    special_ad_categories: campaignData.special_ad_categories,
    buying_type: campaignData.buying_type || 'AUCTION',
    budget_mode: budgetMode,
    creation_mode: creationMode || CREATION_MODES.BUILD_PLAN,
    series_mode: seriesMode || SERIES_MODES.REGULAR,
    start_time: campaignData.start_time,
    stop_time: campaignData.stop_time,
  };

  if (creationMode === CREATION_MODES.CBO_REGULAR_BID) {
    if (campaignData.daily_budget) params.daily_budget = campaignData.daily_budget;
    if (campaignData.lifetime_budget) params.lifetime_budget = campaignData.lifetime_budget;
    const bidStrategy = seriesMode === SERIES_MODES.ROI
      ? (campaignData.bid_strategy || 'LOWEST_COST_WITH_MIN_ROAS')
      : (selectedTemplate?.campaign_bid_strategy || campaignData.bid_strategy);
    if (bidStrategy) {
      params.bid_strategy = normalizeMetaBidStrategy(
        bidStrategy,
        seriesMode === SERIES_MODES.ROI ? 'LOWEST_COST_WITH_MIN_ROAS' : 'LOWEST_COST_WITH_BID_CAP'
      );
    }
    if (seriesMode !== SERIES_MODES.ROI && campaignData.bid_amount != null) {
      params.bid_amount = campaignData.bid_amount;
    }
    if (selectedTemplate?.advantage_campaign_budget != null) {
      params.advantage_campaign_budget = selectedTemplate.advantage_campaign_budget;
    }
    return params;
  }

  if (creationMode === CREATION_MODES.ABO_REGULAR_BID) {
    const sharing = selectedTemplate?.adset_budget_sharing_enabled ?? true;
    params.adset_budget_sharing_enabled = sharing;
    if (seriesMode === SERIES_MODES.ROI) {
      params.bid_strategy = normalizeMetaBidStrategy(
        campaignData.bid_strategy || 'LOWEST_COST_WITH_MIN_ROAS',
        'LOWEST_COST_WITH_MIN_ROAS'
      );
    } else if (sharing) {
      params.bid_strategy = normalizeMetaBidStrategy(
        selectedTemplate?.campaign_bid_strategy || 'LOWEST_COST_WITH_BID_CAP',
        'LOWEST_COST_WITH_BID_CAP'
      );
      const bidAmount = campaignData.bid_amount ?? selectedTemplate?.bid_amount;
      if (bidAmount != null) params.bid_amount = bidAmount;
    }
    return params;
  }

  if (seriesMode === SERIES_MODES.ROI) {
    params.bid_strategy = normalizeMetaBidStrategy(
      campaignData.bid_strategy || 'LOWEST_COST_WITH_MIN_ROAS',
      'LOWEST_COST_WITH_MIN_ROAS'
    );
    return params;
  }

  if (budgetMode === 'CBO') {
    if (campaignData.daily_budget) params.daily_budget = campaignData.daily_budget;
    if (campaignData.lifetime_budget) params.lifetime_budget = campaignData.lifetime_budget;
    if (campaignData.bid_strategy) {
      params.bid_strategy = normalizeMetaBidStrategy(campaignData.bid_strategy);
    }
    if (campaignData.bid_amount) params.bid_amount = campaignData.bid_amount;
    if (selectedTemplate?.advantage_campaign_budget != null) {
      params.advantage_campaign_budget = selectedTemplate.advantage_campaign_budget;
    }
  } else if (selectedTemplate) {
    if (selectedTemplate.advantage_campaign_budget != null) {
      params.advantage_campaign_budget = selectedTemplate.advantage_campaign_budget;
    }
    if (campaignData.bid_strategy) {
      params.bid_strategy = normalizeMetaBidStrategy(campaignData.bid_strategy);
    }
  }
  return params;
};

const AdCreation = () => {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [taskId, setTaskId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [baseName, setBaseName] = useState('');
  const [budgetMode, setBudgetMode] = useState('ABO');
  const [creationMode, setCreationMode] = useState(CREATION_MODES.BUILD_PLAN);
  const [seriesMode, setSeriesMode] = useState(SERIES_MODES.REGULAR);
  const [campaignData, setCampaignData] = useState({});
  const [adsetsData, setAdsetsData] = useState([]);
  const [adsData, setAdsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPages, setLoadingPages] = useState(false);
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accountPages, setAccountPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageId, setPageId] = useState('');
  const [syncingAccountPages, setSyncingAccountPages] = useState(false);
  const [accountPrereqModal, setAccountPrereqModal] = useState({
    open: false,
    issues: [],
  });
  const [manualPageModalOpen, setManualPageModalOpen] = useState(false);
  const [developerPages, setDeveloperPages] = useState([]);
  const [fetchingDeveloperPages, setFetchingDeveloperPages] = useState(false);
  const [manualPageId, setManualPageId] = useState('');
  const [manualPagePick, setManualPagePick] = useState(null);
  const [accountPixels, setAccountPixels] = useState([]);
  const [selectedPixelId, setSelectedPixelId] = useState('');
  const [advertiserIdentities, setAdvertiserIdentities] = useState([]);
  const [selectedAdvertiserIdentityId, setSelectedAdvertiserIdentityId] = useState(undefined);
  const [progress, setProgress] = useState({ total: 0, completed: 0 });
  // 存储广告创建失败信息
  const [errorInfo, setErrorInfo] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [randomSuffix, setRandomSuffix] = useState('');
  const [materials, setMaterials] = useState([]);
  const [creativeCopies, setCreativeCopies] = useState([]);  // 文案库列表
  const [creativeCopyMeta, setCreativeCopyMeta] = useState({
    page: 0,
    total: 0,
    hasMore: true,
    loading: false,
    search: '',
  });
  const [selectedCreativeCopy, setSelectedCreativeCopy] = useState(null);  // 选中的文案库
  const [materialCollections, setMaterialCollections] = useState([]);  // 素材集列表
  const [materialCollectionMeta, setMaterialCollectionMeta] = useState({
    page: 0,
    total: 0,
    hasMore: true,
    loading: false,
  });
  const [selectedMaterialCollection, setSelectedMaterialCollection] = useState(null);  // 选中的素材集
  const [materialAllocationPreview, setMaterialAllocationPreview] = useState(null);
  const [collectionMaterials, setCollectionMaterials] = useState([]);  // 选中素材集的素材列表
  const materialsAllocatedRef = useRef(false);  // 标记素材是否已经分配过
  const [task, setTask] = useState(null);  // 任务数据状态
  
  // 素材匹配和上传状态
  const [matchingMaterials, setMatchingMaterials] = useState(false);
  const [uploadingMaterials, setUploadingMaterials] = useState(false);
  const [materialUploadProgress, setMaterialUploadProgress] = useState(0);
  const [materialsUploaded, setMaterialsUploaded] = useState(false);
  
  // 检查广告是否已经有素材的hash值
  const hasAdMaterialsHash = () => {
    // 检查是否有任何广告已经有素材hash值
    return adsData.some(group => 
      group.some(ad => ad.material_ids.length > 0)
    );
  };
  
  // 防重复加载标志
  const fetchingRef = useRef({
    templates: false,
    materials: false,
    pages: false,
    collectionMaterials: false,
    adAccounts: false,
    creativeCopies: false,
    materialCollections: false,
    task: false
  });
  
  // 组件初始化标志（防止 StrictMode 双重渲染）
  const initializedRef = useRef(false);
  
  // 临时保存的素材集ID（用于延迟恢复）
  const pendingCollectionIdRef = useRef(null);
  
  // 标识是否从草稿箱恢复
  const isFromDraftRef = useRef(false);
  
  // 防止草稿任务重复加载
  const draftTaskLoadedRef = useRef(null);
  
  // 防止步骤3重复加载数据
  const step3DataLoadedRef = useRef(false);
  const step3MaterialsMatchRef = useRef(false);
  const step3CopyLoadedRef = useRef(false);
  const step3PagesReconcileRef = useRef(false);
  const pagesReconcileInFlightRef = useRef(false);
  const taskCompletedRef = useRef(false);
  const creativeCopySearchTimerRef = useRef(null);

  const isAdPageLocked = (ad) =>
    Boolean(ad?.facebook_ad_id) || ad?.status === 'done';

  const isPageSelectable = (page) =>
    page?.is_allocatable !== false && !page?.is_full && (page?.hq_status || 'active') === 'active';

  const sanitizeInvalidAdPageIds = (sourceAds, allowedSet) =>
    (sourceAds || []).map((group) =>
      group.map((ad) => {
        if (isAdPageLocked(ad)) return ad;
        const pid = ad.page_id != null ? String(ad.page_id) : '';
        if (pid && !allowedSet.has(pid)) {
          return { ...ad, page_id: null };
        }
        return ad;
      })
    );

  const buildAdsPageSignature = (data) =>
    (data || [])
      .map((group) =>
        group
          .map(
            (ad) =>
              `${ad.adset_index}-${ad.ad_index}:${ad.page_id || ''}:${ad.status || ''}:${ad.facebook_ad_id || ''}`
          )
          .join(',')
      )
      .join('|');

  // 获取模板列表
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    // 重置所有标志
    draftTaskLoadedRef.current = null;
    isFromDraftRef.current = false;
    materialsAllocatedRef.current = false;
    step3DataLoadedRef.current = false;
    pendingCollectionIdRef.current = null;
    
    fetchTemplates();
    fetchAdAccounts();
    fetchCreativeCopies({ page: 1, append: false });
    fetchMaterialCollections({ page: 1, append: false });
  }, []);

  // 当素材集列表加载完成后，恢复之前保存的素材集
  useEffect(() => {
    console.log('Material collections loaded effect triggered:', {
      materialCollectionsLength: materialCollections.length,
      pendingCollectionId: pendingCollectionIdRef.current,
      selectedMaterialCollection: selectedMaterialCollection ? (selectedMaterialCollection.name || selectedMaterialCollection.id) : null,
      collectionMaterialsLength: collectionMaterials.length
    });
    
    // 如果是从草稿箱进入的，并且素材集列表已加载但还没有选中的素材集
    if (materialCollections.length > 0 && (pendingCollectionIdRef.current || isFromDraftRef.current)) {
      const targetCollectionId = pendingCollectionIdRef.current || (task?.material_collection_id || null);
      if (targetCollectionId) {
        console.log('Attempting to restore material collection:', targetCollectionId);
        const collection = materialCollections.find(c => c.id === targetCollectionId);
        if (collection) {
          console.log('Found material collection to restore:', collection.name);
          setSelectedMaterialCollection(collection);
          console.log('Delayed restoration of material collection:', collection.name);
          // 获取素材集中的素材
          fetchCollectionMaterials();
          // 只有在成功找到并恢复素材集后，才重置pending标志
          pendingCollectionIdRef.current = null;
        } else {
          console.warn('Material collection not found for ID:', targetCollectionId);
          // 即使没有找到对应的素材集，也应该重置pending标志
          pendingCollectionIdRef.current = null;
        }
      }
    }
  }, [materialCollections, task]);

  // 草稿恢复：从草稿箱跳转过来时加载草稿数据
  useEffect(() => {
    const draftTaskId = location.state?.draftTaskId;
    const draftStep = location.state?.currentStep || 1;
    if (draftTaskId && !taskCompletedRef.current) {
      // 如果模板列表已加载，直接恢复
      if (templates.length > 0) {
        console.log('Loading draft task directly, templates loaded');
        loadDraftTask(draftTaskId, draftStep);
      } else {
        // 如果模板列表未加载，等待5秒后直接恢复（不用轮询）
        console.log('Waiting for templates to load, will load draft in 5s');
        const timer = setTimeout(() => {
          loadDraftTask(draftTaskId, draftStep);
        }, 5000);
        
        // 同时监听模板加载，一旦加载完成立即恢复
        const checkTimer = setInterval(() => {
          if (templates.length > 0) {
            clearTimeout(timer);
            clearInterval(checkTimer);
            loadDraftTask(draftTaskId, draftStep);
          }
        }, 100);
      }
    }
  }, [location.state, templates]);

  useEffect(() => {
    step3PagesReconcileRef.current = false;
  }, [taskId]);

  // 步骤3时自动查询关联的公共主页和素材
  useEffect(() => {
    console.log('Step3 useEffect triggered:', { 
      currentStep, 
      selectedAccount, 
      accountPagesLength: accountPages.length,
      selectedMaterialCollection: selectedMaterialCollection ? (selectedMaterialCollection.name || selectedMaterialCollection.id) : null,
      collectionMaterialsLength: collectionMaterials.length,
      step3DataLoaded: step3DataLoadedRef.current
    });
    
    if (currentStep === 3) {
      if (
        selectedAccount &&
        taskId &&
        adsData.length > 0 &&
        !step3PagesReconcileRef.current
      ) {
        step3PagesReconcileRef.current = true;
        reconcilePagesForStep3(selectedAccount, taskId, adsData);
      }

      if (materials.length === 0) {
        fetchMaterials();
      }
      // 如果有选中的素材集，获取素材集中的素材
      if (selectedMaterialCollection && collectionMaterials.length === 0) {
        console.log('Fetching collection materials for:', selectedMaterialCollection.id);
        fetchCollectionMaterials();
      }
      
      // 标记步骤3数据已加载
      step3DataLoadedRef.current = true;
    } else {
      // 不在步骤3，重置标志
      step3DataLoadedRef.current = false;
      step3PagesReconcileRef.current = false;
    }
  }, [
    currentStep,
    selectedAccount,
    taskId,
    buildAdsPageSignature(adsData),
    selectedMaterialCollection,
    collectionMaterials.length,
    materials.length,
  ]);

  // 自动分配素材的逻辑单独放在一个 useEffect 中，只在 collectionMaterials 和 adsData 变化时触发
  useEffect(() => {
    if (currentStep === 3 && selectedMaterialCollection && collectionMaterials.length > 0 && adsData.length > 0) {
      // 检查是否需要分配（只有当有广告没有素材时才分配
      const needAllocation = adsData.some(group => group.some(ad => !ad.material_ids || ad.material_ids.length === 0));
      if (needAllocation && !materialsAllocatedRef.current) {
        // 如果是从草稿箱恢复，只有当有广告缺少素材时才分配
        // 正常创建时，直接分配
        materialsAllocatedRef.current = true;  // 标记已分配
        console.log('Auto allocating materials, isFromDraft:', isFromDraftRef.current, 'needAllocation:', needAllocation);
        allocateMaterialsAuto();
      }
    }
  }, [currentStep, selectedMaterialCollection, collectionMaterials, adsData]);

  // 当步骤改变时重置分配标志（允许重新分配）
  useEffect(() => {
    if (currentStep !== 3) {
      materialsAllocatedRef.current = false;
      isFromDraftRef.current = false;  // 重置草稿箱标志
    }
  }, [currentStep]);

  // 系列级像素：进入步骤 2 时统一写到所有 AdSet
  useEffect(() => {
    if (currentStep === 2 && selectedPixelId && adsetsData.length > 0) {
      const needUpdate = adsetsData.some((adset) => adset.pixel_id !== selectedPixelId);
      if (needUpdate) {
        setAdsetsData(adsetsData.map((adset) => ({ ...adset, pixel_id: selectedPixelId })));
      }
    }
  }, [currentStep, selectedPixelId, adsetsData]);

  const fetchTemplates = async () => {
    if (fetchingRef.current.templates) return;
    fetchingRef.current.templates = true;
    try {
      const res = await api.get('/ad-series-templates/');
      setTemplates(res.data);
    } catch (error) {
      message.error('获取模板失败');
    } finally {
      fetchingRef.current.templates = false;
    }
  };

  const fetchMaterials = async (taskData = null) => {
    if (fetchingRef.current.materials) return;
    fetchingRef.current.materials = true;
    try {
      // 检查是否有选中的素材集或者任务的material_collection_id
      const currentTask = taskData || task;
      const hasAssociatedCollection = selectedMaterialCollection || currentTask?.material_collection_id;
      
      if (hasAssociatedCollection) {
        // 只查询与当前任务关联的素材集的素材
        const collectionId = selectedMaterialCollection?.id || currentTask?.material_collection_id;
        const res = await api.get(`/materials/collections/${collectionId}`);
        setCollectionMaterials(res.data.materials || []);
        // 不更新 materials 状态，保持与 collectionMaterials 一致
      } else {
        // 如果没有关联的素材集，查询所有素材
        const res = await api.get('/materials/', {
          params: { page: 1, page_size: 100 },
        });
        setMaterials(res.data.items || []);
      }
    } catch (error) {
      console.error('获取素材失败:', error); // 添加错误日志
      message.error('获取素材失败');
    } finally {
      fetchingRef.current.materials = false;
    }
  };

  const fetchAdAccounts = async () => {
    if (fetchingRef.current.adAccounts) return;
    fetchingRef.current.adAccounts = true;
    try {
      const res = await api.get('/ad-accounts/', {
        params: { include_fb_usage: false },
      });
      setAdAccounts(res.data);
    } catch (error) {
      message.error('获取广告账户失败');
    } finally {
      fetchingRef.current.adAccounts = false;
    }
  };

  const handleCreativeCopySelect = async (value) => {
    if (!value || value === '__loading__' || value === '__end__') {
      setSelectedCreativeCopy(null);
      return;
    }
    const summary = creativeCopies.find((c) => c.id === value);
    setSelectedCreativeCopy(summary || { id: value });
    try {
      const res = await api.get(`/creative-copy/${value}`);
      setSelectedCreativeCopy(res.data);
    } catch (error) {
      console.error('Fetch creative copy detail error:', error);
    }
  };

  const fetchCreativeCopies = async ({
    page = 1,
    append = false,
    search = creativeCopyMeta.search,
  } = {}) => {
    if (fetchingRef.current.creativeCopies) return;
    fetchingRef.current.creativeCopies = true;
    setCreativeCopyMeta((prev) => ({ ...prev, loading: true, search }));
    try {
      const params = {
        page,
        page_size: CREATIVE_COPY_PAGE_SIZE,
        summary: true,
      };
      if (search?.trim()) {
        params.search = search.trim();
      }
      const res = await api.get('/creative-copy/', { params });
      const items = res.data?.items || res.data || [];
      const total = res.data?.total ?? items.length;
      setCreativeCopies((prev) => (append ? mergeListById(prev, items) : items));
      setCreativeCopyMeta({
        page,
        total,
        hasMore: page * CREATIVE_COPY_PAGE_SIZE < total,
        loading: false,
        search: search || '',
      });
    } catch (error) {
      console.error('获取文案库失败:', error);
      if (!append) {
        message.error('获取文案库失败');
      }
      setCreativeCopyMeta((prev) => ({ ...prev, loading: false }));
    } finally {
      fetchingRef.current.creativeCopies = false;
    }
  };

  const loadMoreCreativeCopies = () => {
    if (
      !creativeCopyMeta.hasMore ||
      creativeCopyMeta.loading ||
      fetchingRef.current.creativeCopies
    ) {
      return;
    }
    fetchCreativeCopies({
      page: creativeCopyMeta.page + 1,
      append: true,
      search: creativeCopyMeta.search,
    });
  };

  const handleCreativeCopyPopupScroll = (e) => {
    const { target } = e;
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 24) {
      loadMoreCreativeCopies();
    }
  };

  const handleCreativeCopySearch = (value) => {
    if (creativeCopySearchTimerRef.current) {
      clearTimeout(creativeCopySearchTimerRef.current);
    }
    creativeCopySearchTimerRef.current = setTimeout(() => {
      fetchCreativeCopies({ page: 1, append: false, search: value });
    }, 300);
  };

  const fetchMaterialCollections = async ({ page = 1, append = false } = {}) => {
    if (fetchingRef.current.materialCollections) return;
    fetchingRef.current.materialCollections = true;
    setMaterialCollectionMeta((prev) => ({ ...prev, loading: true }));
    try {
      const res = await api.get('/materials/collections/', {
        params: { page, page_size: MATERIAL_COLLECTION_PAGE_SIZE },
      });
      const items = res.data?.items || res.data || [];
      const total = res.data?.total ?? items.length;
      setMaterialCollections((prev) => (append ? mergeListById(prev, items) : items));
      setMaterialCollectionMeta({
        page,
        total,
        hasMore: page * MATERIAL_COLLECTION_PAGE_SIZE < total,
        loading: false,
      });
    } catch (error) {
      console.error('获取素材集失败:', error);
      if (!append) {
        message.error('获取素材集失败');
      }
      setMaterialCollectionMeta((prev) => ({ ...prev, loading: false }));
    } finally {
      fetchingRef.current.materialCollections = false;
    }
  };

  const loadMoreMaterialCollections = () => {
    if (
      !materialCollectionMeta.hasMore ||
      materialCollectionMeta.loading ||
      fetchingRef.current.materialCollections
    ) {
      return;
    }
    fetchMaterialCollections({
      page: materialCollectionMeta.page + 1,
      append: true,
    });
  };

  const handleMaterialCollectionPopupScroll = (e) => {
    const { target } = e;
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 24) {
      loadMoreMaterialCollections();
    }
  };

  const fetchMaterialAllocationPreview = async () => {
    if (!selectedMaterialCollection?.id || !selectedAccount) {
      setMaterialAllocationPreview(null);
      return;
    }
    try {
      const adCount =
        (selectedTemplate?.adset_count || 1) * (selectedTemplate?.ad_per_adset || 1);
      const materialsPerAd = selectedTemplate?.material_per_ad || 1;
      const params = { ad_account_id: selectedAccount };
      if (selectedTemplate && adCount > 0) {
        params.ad_count = adCount;
        params.materials_per_ad = materialsPerAd;
      }
      const res = await api.get(
        `/materials/collections/${selectedMaterialCollection.id}/allocation-cursor`,
        { params }
      );
      setMaterialAllocationPreview(res.data || null);
    } catch (error) {
      console.error('Fetch material allocation preview error:', error);
      setMaterialAllocationPreview(null);
    }
  };

  useEffect(() => {
    fetchMaterialAllocationPreview();
  }, [selectedMaterialCollection?.id, selectedAccount, selectedTemplate?.id]);

  const fetchCollectionMaterials = async () => {
    if (!selectedMaterialCollection || fetchingRef.current.collectionMaterials) {
      console.log('Skipping fetchCollectionMaterials - already fetching or no collection selected');
      return;
    }
    
    console.log('Calling fetchCollectionMaterials for:', selectedMaterialCollection.id);
    fetchingRef.current.collectionMaterials = true;
    try {
      const res = await api.get(`/materials/collections/${selectedMaterialCollection.id}`);
      console.log('Collection materials fetched:', res.data.materials);
      setCollectionMaterials(res.data.materials || []);
    } catch (error) {
      console.error('获取素材集素材失败:', error);
      message.error('获取素材集素材失败');
    } finally {
      fetchingRef.current.collectionMaterials = false;
    }
  };

  const allocateMaterialsAuto = () => {
    if (!selectedMaterialCollection || collectionMaterials.length === 0) return;

    const newAdsData = [...adsData];
    let materialIndex =
      materialAllocationPreview?.next_material_index ??
      selectedMaterialCollection.last_material_index ??
      0;
    const startIndex = materialIndex;
    const totalMaterials = collectionMaterials.length;

    for (let i = 0; i < newAdsData.length; i++) {
      for (let j = 0; j < newAdsData[i].length; j++) {
        const ad = newAdsData[i][j];
        if (!ad.material_ids || ad.material_ids.length === 0) {
          const material = collectionMaterials[materialIndex % totalMaterials];
          newAdsData[i][j].material_ids = [material.id];
          materialIndex++;
        }
      }
    }

    setAdsData(newAdsData);
    message.success(`已为 ${materialIndex - startIndex} 个广告自动分配素材`);
  };

  const formatPageLabel = (page) => {
    const pid = page?.page_id ?? page?.id ?? '';
    const name = page?.name || '未命名';
    const count = page?.active_ad_count ?? 0;
    const limit = page?.active_ad_limit ?? 250;
    const remaining = page?.remaining ?? Math.max(0, limit - count);
    return `${name}（${pid}）· ${count}/${limit}，剩余 ${remaining}`;
  };

  const mergePagesFromAssignments = (pages, assignments) => {
    const byId = new Map(
      (pages || []).map((p) => [String(p.page_id || p.id), { ...p, page_id: String(p.page_id || p.id) }]),
    );
    (assignments || []).forEach((a) => {
      const pid = a?.page_id != null ? String(a.page_id) : '';
      if (!pid) return;
      const existing = byId.get(pid);
      const count = a.active_ad_count ?? existing?.active_ad_count ?? 0;
      const limit = a.active_ad_limit ?? existing?.active_ad_limit ?? 250;
      byId.set(pid, {
        ...(existing || {}),
        page_id: pid,
        name: a.page_name || existing?.name || pid,
        active_ad_count: count,
        active_ad_limit: limit,
        remaining: a.remaining ?? Math.max(0, limit - count),
        is_full: a.is_full ?? count >= limit,
      });
    });
    return Array.from(byId.values());
  };

  const fetchAllocatablePages = async (accountId, developerAccountId) => {
    const devId = developerAccountId ?? task?.developer_account_id ?? null;
    const params = devId ? { developer_account_id: devId } : {};
    const res = await api.get(`/ad-accounts/${accountId}/pages`, { params });
    const pages = (res.data || []).filter(isPageSelectable);
    return pages;
  };

  const fetchDeveloperPages = async (accountId, developerAccountId) => {
    setFetchingDeveloperPages(true);
    try {
      const devId = developerAccountId ?? task?.developer_account_id ?? null;
      const params = devId ? { developer_account_id: devId } : {};
      const res = await api.get(`/ad-accounts/${accountId}/developer-pages`, { params });
      const pages = res.data || [];
      setDeveloperPages(pages);
      return pages;
    } catch (error) {
      console.error('Fetch developer pages error:', error);
      message.error(
        '拉取开发者公共主页失败: ' + (error.response?.data?.detail || error.message)
      );
      setDeveloperPages([]);
      return [];
    } finally {
      setFetchingDeveloperPages(false);
    }
  };

  const applyManualPageToAllAds = (page) => {
    const pid = String(page.page_id || page.id || '');
    if (!pid) return;
    const newAds = adsData.map((group) =>
      group.map((ad) => (isAdPageLocked(ad) ? ad : { ...ad, page_id: pid }))
    );
    setAdsData(newAds);
    setPageId(pid);
    setManualPageId(pid);
    setAccountPages([page]);
    setSelectedPage(page);
  };

  const handleOpenManualPageModal = async () => {
    if (!selectedAccount) {
      message.error('请先选择广告账户');
      return;
    }
    setManualPageModalOpen(true);
    setManualPagePick(manualPageId || null);
    setFetchingDeveloperPages(true);
    try {
      const pages = await fetchAllocatablePages(
        selectedAccount,
        task?.developer_account_id
      );
      setDeveloperPages(pages);
    } finally {
      setFetchingDeveloperPages(false);
    }
  };

  const handleConfirmManualPage = () => {
    const page = developerPages.find(
      (p) => String(p.page_id || p.id) === String(manualPagePick)
    );
    if (!page) {
      message.warning('请选择公共主页');
      return;
    }
    if (!isPageSelectable(page)) {
      message.warning('该主页已暂停或已达在投上限，请选择其他主页');
      return;
    }
    applyManualPageToAllAds(page);
    setManualPageModalOpen(false);
    message.success('已为全部待创建广告设置公共主页');
  };

  const fetchAccountPages = async (accountId, { silent = false } = {}) => {
    if (fetchingRef.current.pages) return [];
    fetchingRef.current.pages = true;
    setLoadingPages(true);
    console.log('Fetching allocatable pages for account:', accountId);
    try {
      const pages = await fetchAllocatablePages(accountId);
      console.log('Allocatable pages:', pages);
      setAccountPages(pages);
      if (pages.length > 0) {
        setSelectedPage(pages[0]);
        setPageId(pages[0].page_id);
      } else {
        setSelectedPage(null);
        setPageId('');
      }
      return pages;
    } catch (error) {
      console.error('Fetch pages error:', error);
      const detail = error.response?.data?.detail || error.message;
      if (!silent) {
        const text = typeof detail === 'string' ? detail : String(detail);
        if (text.includes('尚未同步')) {
          message.warning(text);
        } else {
          message.error('获取公共主页失败: ' + text);
        }
      }
      setAccountPages([]);
      return [];
    } finally {
      fetchingRef.current.pages = false;
      setLoadingPages(false);
    }
  };

  const handleSyncAccountPages = async ({ silent = false, accountId = selectedAccount } = {}) => {
    if (!accountId) {
      if (!silent) message.error('请先选择广告账户');
      return { success: false, pages: [], pixels: [] };
    }
    setSyncingAccountPages(true);
    try {
      const res = await api.post(`/ad-accounts/${accountId}/fetch-pages`);
      if (!silent) {
        message.success(res.data?.message || '已同步可推广主页与像素到本地');
      }
      const pages = await fetchAccountPages(accountId, { silent: true });
      await fetchAccountPixels(accountId, { silent: true });
      if (pages.length === 0 && !silent) {
        message.warning(
          '同步完成，但当前无可分配主页（可能均已暂停或已达在投上限），请在「公共主页」检查状态'
        );
      }
      if (taskId) {
        await reconcilePagesForStep3(accountId, taskId, adsData);
      }
      if (pages.length > 0 && !silent) {
        message.success(`已加载 ${pages.length} 个可分配主页`);
      }
      return {
        success: true,
        pages,
        pixels: res.data?.pixels || [],
      };
    } catch (error) {
      if (!silent) {
        message.error(
          error.response?.data?.detail || '同步该账户可推广主页与像素失败'
        );
      }
      return { success: false, pages: [], pixels: [] };
    } finally {
      setSyncingAccountPages(false);
    }
  };

  const checkAccountPrerequisites = async (accountId, developerAccountId) => {
    const issues = [];
    let pages = [];
    let pixels = [];
    const devId = developerAccountId ?? task?.developer_account_id ?? null;

    try {
      pages = await fetchAllocatablePages(accountId, devId);
      if (pages.length === 0) {
        issues.push(
          devId
            ? '当前开发者在该账户下没有可分配的公共主页（请检查开发者主页关联）'
            : '当前账户没有可分配的公共主页（可能尚未同步，或主页均已暂停/达在投上限）'
        );
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || '';
      const text = typeof detail === 'string' ? detail : String(detail);
      if (text.includes('尚未同步')) {
        issues.push('尚未同步可推广公共主页');
      } else {
        issues.push(`公共主页检查失败：${text}`);
      }
    }

    try {
      const res = await api.get(`/ad-accounts/${accountId}/pixels`);
      pixels = res.data || [];
      if (pixels.length === 0) {
        issues.push('尚未同步像素，或该账户下没有可用像素');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || '';
      issues.push(`像素检查失败：${detail}`);
    }

    return {
      ok: issues.length === 0,
      issues,
      pages,
      pixels,
    };
  };

  const handleAccountPrereqSync = async () => {
    const result = await handleSyncAccountPages({ silent: false, accountId: selectedAccount });
    if (!result.success) {
      return;
    }
    const validation = await checkAccountPrerequisites(selectedAccount);
    if (validation.ok) {
      setAccountPrereqModal({ open: false, issues: [] });
      setAccountPages(validation.pages);
      setAccountPixels(validation.pixels);
      await executeCreateTask({
        defaultPixelId: selectedPixelId || validation.pixels[0]?.pixel_id,
      });
      return;
    }
    setAccountPrereqModal({ open: true, issues: validation.issues });
    message.warning('同步完成，但该账户仍缺少创建系列所需的数据，请检查后重试');
  };

  const applyPageAssignments = (assignments, basePages, adsSnapshot) => {
    const sourceAds = adsSnapshot || adsData;
    if (!assignments?.length || !sourceAds.length) return;
    const map = new Map(
      assignments.map((a) => [
        `${a.adset_index}-${a.ad_index}`,
        a.page_id != null ? String(a.page_id) : '',
      ])
    );
    const updatedAds = sourceAds.map((group, i) =>
      group.map((ad, j) => {
        if (isAdPageLocked(ad)) return ad;
        const key = `${i}-${j}`;
        if (!map.has(key)) return ad;
        const pid = map.get(key);
        return { ...ad, page_id: pid || null };
      })
    );
    setAdsData(updatedAds);
    const base = basePages || accountPages;
    setAccountPages(mergePagesFromAssignments(base, assignments));
  };

  const allocatePagesForTask = async (taskIdStr, basePages, adsSnapshot) => {
    const sourceAds = adsSnapshot || adsData;
    if (!taskIdStr || !sourceAds.length) return;
    try {
      const res = await api.post(`/ad-creation-tasks/${taskIdStr}/allocate-pages`);
      applyPageAssignments(res.data?.assignments || [], basePages, sourceAds);
    } catch (error) {
      console.error('Allocate pages error:', error);
      message.error(
        '分配公共主页失败: ' + (error.response?.data?.detail || error.message)
      );
    }
  };

  const reconcilePagesForStep3 = async (accountId, taskIdStr, currentAdsData, developerAccountId) => {
    if (!accountId || !taskIdStr || !currentAdsData?.length) return;
    if (pagesReconcileInFlightRef.current) return;
    pagesReconcileInFlightRef.current = true;
    setLoadingPages(true);
    const devId = developerAccountId ?? task?.developer_account_id ?? null;
    try {
      const allowedPages = await fetchAllocatablePages(accountId, devId);
      const allowedSet = new Set(
        allowedPages.map((p) => String(p.page_id || p.id))
      );
      if (allowedPages.length > 0) {
        setAccountPages(allowedPages);
        setManualPageId('');
        setSelectedPage(allowedPages[0]);
        setPageId(allowedPages[0].page_id);
      } else if (!manualPageId) {
        setAccountPages([]);
      }
      if (manualPageId) {
        allowedSet.add(String(manualPageId));
      }

      const sanitized = sanitizeInvalidAdPageIds(currentAdsData, allowedSet);
      const hadInvalid = currentAdsData.some((group) =>
        group.some((ad) => {
          if (isAdPageLocked(ad)) return false;
          const pid = ad.page_id != null ? String(ad.page_id) : '';
          return pid && !allowedSet.has(pid);
        })
      );
      setAdsData(sanitized);

      const needAllocate = sanitized.some((group) =>
        group.some((ad) => {
          if (isAdPageLocked(ad)) return false;
          const pid = ad.page_id != null ? String(ad.page_id) : '';
          return !pid;
        })
      );

      if (needAllocate) {
        if (allowedPages.length === 0) {
          message.warning(
            devId
              ? '当前开发者在该账户下没有可分配的公共主页（请检查开发者主页关联与账户同步结果）'
              : '当前无可分配的公共主页。请先在「设置 → 广告账户 → 同步信息」同步可推广主页，或在「公共主页」启用可用主页'
          );
          return;
        }
        await allocatePagesForTask(taskIdStr, allowedPages, sanitized);
        if (hadInvalid) {
          message.info('已暂停或无效的主页已清除，并重新分配可推广主页');
        } else {
          message.info('已根据可推广主页列表分配公共主页');
        }
      } else if (hadInvalid) {
        message.info('已清除暂停或不可用的公共主页');
      }
    } catch (error) {
      console.error('Reconcile pages error:', error);
      const detail = error.response?.data?.detail || error.message;
      const text = typeof detail === 'string' ? detail : String(detail);
      if (text.includes('尚未同步')) {
        message.warning(text);
      } else {
        message.error('同步公共主页失败: ' + text);
      }
    } finally {
      pagesReconcileInFlightRef.current = false;
      setLoadingPages(false);
    }
  };

  const fetchAccountPixels = async (accountId, { silent = false, preferPixelId = null } = {}) => {
    if (!accountId) return;
    console.log('Fetching pixels for account:', accountId);
    try {
      const res = await api.get(`/ad-accounts/${accountId}/pixels`);
      console.log('Pixels response:', res.data);
      const pixels = res.data || [];
      setAccountPixels(pixels);

      const preferred =
        preferPixelId
        || selectedPixelId
        || '';
      const stillValid = preferred && pixels.some((p) => p.pixel_id === preferred);
      const nextPixelId = stillValid ? preferred : (pixels[0]?.pixel_id || '');
      setSelectedPixelId(nextPixelId);

      if (nextPixelId && adsetsData.length > 0) {
        setAdsetsData(adsetsData.map((adset) => ({ ...adset, pixel_id: nextPixelId })));
      }
      return pixels;
    } catch (error) {
      console.error('Fetch pixels error:', error);
      setAccountPixels([]);
      setSelectedPixelId('');
      const detail = error.response?.data?.detail || error.message;
      if (!silent && detail) {
        message.warning(
          typeof detail === 'string' && detail.includes('尚未同步')
            ? detail
            : `获取像素失败: ${detail}（可先在设置中同步广告账户信息，或在 BM 资产中「同步到账户」）`
        );
      }
      return [];
    }
  };

  const fetchAdvertiserIdentities = async (accountId, { preferIdentityId } = {}) => {
    if (!accountId) {
      setAdvertiserIdentities([]);
      setSelectedAdvertiserIdentityId(undefined);
      return [];
    }
    try {
      let account = adAccounts.find((a) => a.account_id === accountId);
      let bmId = account?.bm_id;
      if (!bmId) {
        const accRes = await api.get('/ad-accounts/', {
          params: { include_fb_usage: false },
        });
        const list = accRes.data || [];
        account = list.find((a) => a.account_id === accountId);
        bmId = account?.bm_id;
        if (list.length) {
          setAdAccounts(list);
        }
      }
      if (!bmId) {
        setAdvertiserIdentities([]);
        return [];
      }
      const res = await api.get('/advertiser-identities/', {
        params: { bm_id: bmId, status: 'active', limit: 200 },
      });
      const items = res.data?.items || [];
      setAdvertiserIdentities(items);

      const preferred =
        preferIdentityId !== undefined
          ? preferIdentityId
          : selectedAdvertiserIdentityId;
      if (preferred != null && preferred !== '' && items.some((item) => item.id === preferred)) {
        setSelectedAdvertiserIdentityId(preferred);
      } else if (preferIdentityId !== undefined) {
        setSelectedAdvertiserIdentityId(
          preferred != null && preferred !== '' ? preferred : undefined
        );
      }
      return items;
    } catch (error) {
      console.error('Fetch advertiser identities error:', error);
      setAdvertiserIdentities([]);
      return [];
    }
  };

  // 加载草稿任务数据
  const loadDraftTask = async (draftTaskId, draftStep) => {
    // 防止重复加载同一个任务
    if (draftTaskLoadedRef.current === draftTaskId) {
      console.log('Draft task already loaded, skipping:', draftTaskId);
      return;
    }
    // 防止并发加载
    if (fetchingRef.current.task) {
      console.log('Task fetch already in progress, skipping');
      return;
    }
    
    fetchingRef.current.task = true;
    draftTaskLoadedRef.current = draftTaskId;  // 立即标记已加载
    step3PagesReconcileRef.current = false;

    console.log('loadDraftTask called:', { draftTaskId, draftStep });
    isFromDraftRef.current = true;  // 标记是从草稿箱恢复的
    materialsAllocatedRef.current = false;  // 重置分配标志
    setLoading(true);
    try {
      const res = await api.get(`/ad-creation-tasks/${draftTaskId}`);
      console.log('Full API response:', res);
      console.log('Task response data:', res.data);
      console.log('Task keys:', Object.keys(res.data));
      console.log('ad_account_id from API:', res.data.ad_account_id);
      console.log('Full task object:', JSON.stringify(res.data, null, 2));
      const fetchedTask = res.data;
      if (!fetchedTask) {
        message.error('草稿任务不存在');
        return;
      }
      setTask(fetchedTask);

      // 恢复基础状态
      setTaskId(fetchedTask.task_id);
      setBaseName(fetchedTask.base_name || '');
      setBudgetMode(fetchedTask.budget_mode || 'CBO');
      setCreationMode(fetchedTask.creation_mode || CREATION_MODES.BUILD_PLAN);
      setSeriesMode(
        fetchedTask.series_mode
        || fetchedTask.campaign_params?.series_mode
        || SERIES_MODES.REGULAR
      );
      setRandomSuffix(fetchedTask.random_suffix || '');
      
      // 恢复广告账户
      console.log('Restoring ad_account_id:', fetchedTask.ad_account_id);
      if (fetchedTask.ad_account_id) {
        setSelectedAccount(fetchedTask.ad_account_id);
        console.log('Set selectedAccount to:', fetchedTask.ad_account_id);
        // 步骤3及以后不需要获取像素列表，只获取公共主页列表
        if (draftStep < 3) {
          // 只有步骤1和2需要获取像素
          console.log('Draft step < 3, fetching account pixels...');
          const preferPixel =
            (fetchedTask.adsets || [])
              .map((a) => a?.params?.pixel_id)
              .find(Boolean) || null;
          fetchAccountPixels(fetchedTask.ad_account_id, { preferPixelId: preferPixel });
          const preferIdentity =
            fetchedTask.campaign_params?.advertiser_identity_id
            || (fetchedTask.adsets || [])
              .map((a) => a?.params?.advertiser_identity_id)
              .find((id) => id != null)
            || null;
          fetchAdvertiserIdentities(fetchedTask.ad_account_id, {
            preferIdentityId: preferIdentity,
          });
        } else {
          console.log('Draft step >= 3, skipping account pixels fetch');
        }
        // 步骤3 由 reconcilePagesForStep3 统一拉取可分配主页并校验
        if (draftStep < 3) {
          fetchAccountPages(fetchedTask.ad_account_id);
        }
      }

      // 恢复选中的素材集
      if (fetchedTask.material_collection_id) {
        // 标记是从草稿箱进入的，以便后续useEffect能正确处理
        isFromDraftRef.current = true;
        // 如果素材集列表已加载，直接恢复
        if (materialCollections.length > 0) {
          const collection = materialCollections.find(c => c.id === fetchedTask.material_collection_id);
          if (collection) {
            setSelectedMaterialCollection(collection);
            console.log('Restored material collection:', collection.name);
            // 立即调用，确保素材能够正确加载
            fetchCollectionMaterials();
          }
        } else {
          // 如果素材集列表还没加载，先保存ID到ref，稍后恢复
          pendingCollectionIdRef.current = fetchedTask.material_collection_id;
          console.log('Saved material_collection_id to pending:', fetchedTask.material_collection_id);
        }
      }

      // 恢复 Campaign 数据
      if (fetchedTask.campaign_params) {
        setCampaignData({
          name: fetchedTask.campaign_name || '',
          display_name: fetchedTask.campaign_display_name || '',
          objective: fetchedTask.campaign_params.objective || 'OUTCOME_TRAFFIC',
          buying_type: fetchedTask.campaign_params.buying_type || 'AUCTION',
          bid_strategy: fetchedTask.campaign_params.bid_strategy || 'LOWEST_COST_WITHOUT_CAP',
          special_ad_categories: fetchedTask.campaign_params.special_ad_categories || [],
          daily_budget: fetchedTask.campaign_params.daily_budget,
          lifetime_budget: fetchedTask.campaign_params.lifetime_budget,
          bid_amount: fetchedTask.campaign_params.bid_amount,
        });
      }

      // 恢复模板选择
      let taskTemplate = fetchedTask.template;
      
      // 如果后端没有返回 template 数据，尝试从前端缓存获取
      if (!taskTemplate && fetchedTask.template_id && templates.length > 0) {
        const template = templates.find(t => t.id === fetchedTask.template_id);
        if (template) {
          setSelectedTemplate(template);
          taskTemplate = template;
        }
      }
      
      // 如果后端返回了 template 数据，也设置 selectedTemplate
      if (taskTemplate && taskTemplate.id) {
        setSelectedTemplate(taskTemplate);
      }
      
      // 确保 taskTemplate 有默认值
      if (!taskTemplate) {
        taskTemplate = {
          adset_count: 1,
          ad_per_adset: 1,
          adset_configs: []
        };
      }

      // 恢复 AdSets 数据
      const restoredSeriesMode = getEffectiveSeriesMode(
        taskTemplate,
        fetchedTask.series_mode || fetchedTask.campaign_params?.series_mode
      );
      const isRestoredRoi = restoredSeriesMode === SERIES_MODES.ROI;
      if (fetchedTask.adsets && fetchedTask.adsets.length > 0) {
        const restoredAdsets = fetchedTask.adsets.map((saved, i) => {
          // 获取模板配置作为默认值
          const templateCfg = taskTemplate?.adset_configs?.[Math.min(i, (taskTemplate?.adset_configs?.length || 1) - 1)] || {};
          // 获取已保存的配置（覆盖模板默认值）
          const savedCfg = saved.params || {};
          
          // 合并配置：模板默认值 + 已保存值（已保存值优先）
          const cfg = {
            ...templateCfg,
            ...savedCfg,
            // targeting 需要深度合并
            targeting: {
              ...(templateCfg.targeting || { geo_locations: { countries: ['US'] }, age_min: 18, age_max: 65, genders: [0, 1] }),
              ...(savedCfg.targeting || {}),
            },
            // placements 也需要合并
            placements: {
              ...(templateCfg.placements || {}),
              ...(savedCfg.placements || {}),
            },
            // device_targeting 也需要合并
            device_targeting: {
              ...(templateCfg.device_targeting || { devices: ['mobile', 'desktop'], os: [] }),
              ...(savedCfg.device_targeting || {}),
            }
          };
          
          // 处理 publisher_platforms（从 placements 或 targeting 获取）
          const platforms = cfg.placements?.publisher_platforms || cfg.targeting?.publisher_platforms || ['facebook', 'instagram'];
          if (platforms && platforms.length > 0) {
            cfg.targeting['publisher_platforms'] = platforms;
          }
          
          // 处理 locales 格式（对象数组 -> 字符串数组）
          if (cfg.targeting.locales) {
            cfg.targeting.locales = normalizeLocales(cfg.targeting.locales);
          }
          
          return applyRoiAdsetDefaults({
            index: i,
            name: saved.name || `${fetchedTask.base_name}-${fetchedTask.random_suffix}-${i + 1}`,
            display_name: saved.display_name || `组${i + 1}`,
            schedule: saved.schedule?.start_time ? [moment(saved.schedule.start_time), saved.schedule.end_time ? moment(saved.schedule.end_time) : null] : 
                      (templateCfg.start_time ? [moment(templateCfg.start_time), templateCfg.end_time ? moment(templateCfg.end_time) : null] : [moment(), null]),
            targeting: cfg.targeting,
            optimization_goal: cfg.optimization_goal || 'LINK_CLICKS',
            billing_event: cfg.billing_event || 'IMPRESSIONS',
            bid_strategy: cfg.bid_strategy || 'LOWEST_COST_WITHOUT_CAP',
            daily_budget: cfg.daily_budget || templateCfg.daily_budget || 50,
            lifetime_budget: cfg.lifetime_budget || templateCfg.lifetime_budget,
            bid_amount: cfg.bid_amount || templateCfg.bid_amount,
            roi_coefficient: cfg.roi_coefficient != null
              ? Number(cfg.roi_coefficient)
              : (templateCfg.roi_coefficient != null ? Number(templateCfg.roi_coefficient) : 1.5),
            conversion_location: cfg.conversion_location || 'WEBSITE',
            performance_goal: cfg.performance_goal || 'MAXIMIZE_CONVERSIONS',
            pixel_id: cfg.pixel_id || '',
            advertiser_identity_id: cfg.advertiser_identity_id ?? null,
            conversion_event: cfg.conversion_event || 'PURCHASE',
            attribution_setting: cfg.attribution_setting || '7D_CLICK_1D_VIEW',
            advantage_audience: cfg.advantage_audience ?? templateCfg.advantage_audience ?? 0,
            device_targeting: cfg.device_targeting,
            facebook_adset_id: saved.adset_id,
            status: saved.status,
          }, isRestoredRoi);
        });
        setAdsetsData(restoredAdsets);
        const seriesPixel = restoredAdsets.find((a) => a.pixel_id)?.pixel_id || '';
        if (seriesPixel) {
          setSelectedPixelId(seriesPixel);
        }
        const seriesIdentity =
          fetchedTask.campaign_params?.advertiser_identity_id
          || restoredAdsets.find((a) => a.advertiser_identity_id)?.advertiser_identity_id;
        if (seriesIdentity != null) {
          setSelectedAdvertiserIdentityId(seriesIdentity);
        }
      } else if (taskTemplate && taskTemplate.adset_count > 0) {
        // 如果数据库中没有 AdSets 数据，根据模板重新初始化
        const initialAdsets = [];
        for (let i = 0; i < taskTemplate.adset_count; i++) {
          const cfg = taskTemplate.adset_configs?.[Math.min(i, (taskTemplate.adset_configs?.length || 1) - 1)] || {};
          
          // 从模板配置构建 targeting
          const targeting = {
            geo_locations: { countries: ['US'] },
            age_min: 18,
            age_max: 65,
            genders: [0, 1],
            ...((cfg.targeting) || {})
          };
          
          // 添加 publisher_platforms
          const platforms = cfg.placements?.publisher_platforms || cfg.targeting?.publisher_platforms || ['facebook', 'instagram'];
          if (platforms && platforms.length > 0) {
            targeting['publisher_platforms'] = platforms;
          }
          
          // 处理 locales 格式（对象数组 -> 字符串数组）
          if (targeting.locales) {
            targeting.locales = normalizeLocales(targeting.locales);
          }
          
          initialAdsets.push(applyRoiAdsetDefaults({
            index: i,
            name: `${fetchedTask.base_name}-${fetchedTask.random_suffix}-${i + 1}`,
            display_name: cfg.name || `组${i + 1}`,
            schedule: cfg.start_time ? [moment(cfg.start_time), cfg.end_time ? moment(cfg.end_time) : null] : [moment(), null],
            targeting: targeting,
            optimization_goal: cfg.optimization_goal || 'LINK_CLICKS',
            billing_event: cfg.billing_event || 'IMPRESSIONS',
            bid_strategy: cfg.bid_strategy || 'LOWEST_COST_WITHOUT_CAP',
            daily_budget: cfg.daily_budget || 50,
            lifetime_budget: cfg.lifetime_budget,
            bid_amount: cfg.bid_amount,
            roi_coefficient: cfg.roi_coefficient != null
              ? Number(cfg.roi_coefficient)
              : (taskTemplate?.roi_coefficient != null ? Number(taskTemplate.roi_coefficient) : 1.5),
            conversion_location: cfg.conversion_location || 'WEBSITE',
            performance_goal: cfg.performance_goal || 'MAXIMIZE_CONVERSIONS',
            pixel_id: cfg.pixel_id || '',
            advertiser_identity_id: cfg.advertiser_identity_id ?? null,
            conversion_event: cfg.conversion_event || 'PURCHASE',
            attribution_setting: cfg.attribution_setting || '7D_CLICK_1D_VIEW',
            advantage_audience: cfg.advantage_audience ?? 0,
            device_targeting: cfg.device_targeting || { devices: ['mobile', 'desktop'], os: [] },
            status: 'pending',
            facebook_adset_id: null,
          }, isRestoredRoi));
        }
        setAdsetsData(initialAdsets);
      }

      // 恢复 Ads 数据
      let restoredAdsForPages = null;
      if (fetchedTask.ads && fetchedTask.ads.length > 0) {
        const templateAdPerAdset = taskTemplate?.ad_per_adset || 1;
        const templateAdsetCount = taskTemplate?.adset_count || 1;
        const restoredAds = [];
        for (let i = 0; i < templateAdsetCount; i++) {
          const group = fetchedTask.ads
            .filter(a => a.adset_index === i)
            .sort((a, b) => a.ad_index - b.ad_index)
            .map(ad => ({
              adset_index: ad.adset_index,
              ad_index: ad.ad_index,
              name: ad.name,
              display_name: ad.display_name || `广告${ad.ad_index + 1}`,
              message: ad.message || '',
              messages: ad.params?.messages || [ad.message || ''],
              headline: ad.headline || '',
              headlines: ad.params?.headlines || [ad.headline || ''],
              descriptions: ad.params?.descriptions || [],
              call_to_action: ad.call_to_action || 'SEE_DETAILS',
              link_url: ad.link_url || '',
              material_ids: ad.material_ids || [],
              page_id: ad.page_id || null,
              status: ad.ad_id ? 'done' : ad.status,
              facebook_ad_id: ad.ad_id,
              creative_id: ad.creative_id,
              error_message: ad.error_message, // 新增：恢复广告的错误信息
            }));
          restoredAds.push(group);
        }
        setAdsData(restoredAds);
        restoredAdsForPages = restoredAds;

        // 初始化错误信息状态
        const errors = [];
        fetchedTask.ads.forEach(ad => {
          if (ad.status === 'failed' && ad.error_message) {
            errors.push({
              adset_index: ad.adset_index,
              ad_index: ad.ad_index,
              error: ad.error_message
            });
          }
        });
        setErrorInfo(errors);
      } else if (taskTemplate && taskTemplate.adset_count > 0 && taskTemplate.ad_per_adset > 0) {
        // 如果数据库中没有 Ads 数据，根据模板重新初始化
        // 支持多个文案和标题（用换行分隔）
        const messages = taskTemplate.message_template ? taskTemplate.message_template.split('\\n').filter(m => m.trim()) : [''];
        const headlines = taskTemplate.headline_template ? taskTemplate.headline_template.split('\\n').filter(h => h.trim()) : [''];
        
        // 确保公共主页数据已获取
        if (accountPages.length === 0 && selectedAccount) {
          fetchAccountPages(selectedAccount);
        }
        
        const initialAds = [];
        for (let i = 0; i < taskTemplate.adset_count; i++) {
          const group = [];
          for (let j = 0; j < taskTemplate.ad_per_adset; j++) {
            // 循环使用文案和标题
            const messageIndex = j % messages.length;
            const headlineIndex = j % headlines.length;
            
            group.push({
              adset_index: i,
              ad_index: j,
              name: `${fetchedTask.base_name}-${i + 1}-${j + 1}`,
              display_name: `广告${j + 1}`,
              message: messages[messageIndex] || '',
              messages: [messages[messageIndex] || ''],
              headline: headlines[headlineIndex] || '',
              headlines: [headlines[headlineIndex] || ''],
              descriptions: [],
              call_to_action: taskTemplate.call_to_action || 'SEE_DETAILS',
              link_url: taskTemplate.link_url || '',
              material_ids: [],
              page_id: null,
              status: 'pending',
              facebook_ad_id: null,
              creative_id: null,
            });
          }
          initialAds.push(group);
        }
        setAdsData(initialAds);
      }

      // 设置进度
      if (taskTemplate) {
        const totalAds = (taskTemplate.adset_count || 1) * (taskTemplate.ad_per_adset || 1);
        setProgress({ total: totalAds, completed: fetchedTask.ads?.filter(a => a.ad_id).length || 0 });
      }

      // 判断步骤
      let targetStep = draftStep || 1;
      if (fetchedTask.campaign_id) {
        const expectedAdsetCount = taskTemplate?.adset_count || 1;
        const createdAdsetCount = (fetchedTask.adsets || []).filter(a => a.adset_id).length;
        if (createdAdsetCount >= expectedAdsetCount) {
          targetStep = 3;
        } else {
          targetStep = 2;
        }
      }
      setCurrentStep(targetStep);

      if (
        targetStep >= 3 &&
        fetchedTask.ad_account_id &&
        restoredAdsForPages?.length
      ) {
        step3PagesReconcileRef.current = true;
        await reconcilePagesForStep3(
          fetchedTask.ad_account_id,
          fetchedTask.task_id,
          restoredAdsForPages,
          fetchedTask.developer_account_id
        );
      }

      // 获取与任务关联的素材数据
      fetchMaterials(fetchedTask);
      message.success('草稿已恢复');
    } catch (error) {
      message.error('加载草稿失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
      fetchingRef.current.task = false;
    }
  };

  // ========== 步骤 0: 创建任务 ==========
  const executeCreateTask = async ({ defaultPixelId } = {}) => {
    setLoading(true);
    try {
      const response = await api.post('/ad-creation-tasks/', {
        template_id: selectedTemplate.id,
        base_name: baseName,
        budget_mode: selectedTemplate.budget_mode || budgetMode,
        creation_mode: selectedTemplate.creation_mode || CREATION_MODES.BUILD_PLAN,
        ad_account_id: selectedAccount,
        creative_copy_id: selectedCreativeCopy?.id || null,
        material_collection_id: selectedMaterialCollection?.id || null,
      });

      const task = response.data;
      setTaskId(task.task_id);
      setTask(task);
      setRandomSuffix(task.random_suffix);

      if (task.developer_account_name && task.application_name) {
        message.info(
          `本系列已绑定：开发者「${task.developer_account_name}」/ 应用「${task.application_name}」`
        );
      }
      
      setBudgetMode(selectedTemplate.budget_mode || budgetMode);
      setCreationMode(selectedTemplate.creation_mode || CREATION_MODES.BUILD_PLAN);
      setSeriesMode(selectedTemplate.series_mode || SERIES_MODES.REGULAR);

      const campaignName = `${baseName}-${task.random_suffix}-${moment().format('M.D')}`;
      const isCboRegular = selectedTemplate.creation_mode === CREATION_MODES.CBO_REGULAR_BID;
      const isRoiSeries = (selectedTemplate.series_mode || SERIES_MODES.REGULAR) === SERIES_MODES.ROI;
      setCampaignData({
        name: campaignName,
        display_name: baseName,
        objective: isRoiSeries
          ? (['OUTCOME_SALES', 'OUTCOME_APP_PROMOTION'].includes(selectedTemplate.objective)
            ? selectedTemplate.objective
            : 'OUTCOME_SALES')
          : (selectedTemplate.objective || 'OUTCOME_TRAFFIC'),
        buying_type: selectedTemplate.buying_type || 'AUCTION',
        bid_strategy: isRoiSeries
          ? 'LOWEST_COST_WITH_MIN_ROAS'
          : isCboRegular
            ? normalizeMetaBidStrategy(selectedTemplate.campaign_bid_strategy, 'LOWEST_COST_WITH_BID_CAP')
            : (selectedTemplate.bid_strategy || 'LOWEST_COST_WITHOUT_CAP'),
        special_ad_categories: selectedTemplate.special_ad_categories || [],
        daily_budget: selectedTemplate.campaign_daily_budget,
        lifetime_budget: selectedTemplate.campaign_lifetime_budget,
        bid_amount: isRoiSeries ? null : selectedTemplate.bid_amount,
      });

      const templateConfigs = selectedTemplate.adset_configs || [];
      const initialAdsets = [];
      for (let i = 0; i < selectedTemplate.adset_count; i++) {
        const cfg = templateConfigs[Math.min(i, templateConfigs.length - 1)] || {};
        
        const targeting = {
          geo_locations: { countries: ['US'] },
          age_min: 18,
          age_max: 65,
          genders: [0, 1],
          ...(cfg.targeting || {})
        };
        
        const platforms = cfg.placements?.publisher_platforms || cfg.targeting?.publisher_platforms || ['facebook', 'instagram'];
        if (platforms && platforms.length > 0) {
          targeting['publisher_platforms'] = platforms;
        }
        
        if (targeting.locales) {
          targeting.locales = normalizeLocales(targeting.locales);
        }
        
        initialAdsets.push(applyRoiAdsetDefaults({
          index: i,
          name: `${baseName}-${task.random_suffix}-${i + 1}`,
          display_name: cfg.name || `组${i + 1}`,
          schedule: cfg.start_time ? [moment(cfg.start_time), cfg.end_time ? moment(cfg.end_time) : null] : [moment(), null],
          targeting: targeting,
          optimization_goal: cfg.optimization_goal || 'LINK_CLICKS',
          billing_event: cfg.billing_event || 'IMPRESSIONS',
          use_dynamic_creative: cfg.use_dynamic_creative || false,
          bid_strategy: selectedTemplate.creation_mode === CREATION_MODES.ABO_REGULAR_BID
            ? undefined
            : (cfg.bid_strategy || 'LOWEST_COST_WITHOUT_CAP'),
          daily_budget: selectedTemplate.creation_mode === CREATION_MODES.CBO_REGULAR_BID
            ? undefined
            : (cfg.daily_budget || 50),
          lifetime_budget: selectedTemplate.creation_mode === CREATION_MODES.CBO_REGULAR_BID
            ? undefined
            : cfg.lifetime_budget,
          bid_amount: selectedTemplate.creation_mode === CREATION_MODES.ABO_REGULAR_BID
            ? (selectedTemplate.bid_amount ?? cfg.bid_amount)
            : selectedTemplate.creation_mode === CREATION_MODES.CBO_REGULAR_BID
              ? (selectedTemplate.bid_amount ?? cfg.bid_amount)
              : cfg.bid_amount,
          roi_coefficient: cfg.roi_coefficient != null
            ? Number(cfg.roi_coefficient)
            : (selectedTemplate.roi_coefficient != null
              ? Number(selectedTemplate.roi_coefficient)
              : 1.5),
          conversion_location: cfg.conversion_location || 'WEBSITE',
          performance_goal: cfg.performance_goal || 'MAXIMIZE_CONVERSIONS',
          pixel_id: defaultPixelId || selectedPixelId || accountPixels[0]?.pixel_id || '',
          advertiser_identity_id:
            selectedAdvertiserIdentityId != null && selectedAdvertiserIdentityId !== ''
              ? selectedAdvertiserIdentityId
              : null,
          conversion_event: cfg.conversion_event || 'PURCHASE',
          attribution_setting: cfg.attribution_setting || '7D_CLICK_1D_VIEW',
          advantage_audience: cfg.advantage_audience ?? 0,
          device_targeting: cfg.device_targeting || { devices: ['mobile', 'desktop'], os: [] },
          status: 'pending',
          facebook_adset_id: null,
        }, isRoiSeries));
      }
      setAdsetsData(initialAdsets);

      const messages = selectedTemplate.message_template ? selectedTemplate.message_template.split('\\n').filter(m => m.trim()) : [''];
      const headlines = selectedTemplate.headline_template ? selectedTemplate.headline_template.split('\\n').filter(h => h.trim()) : [''];
      
      const initialAds = [];
      for (let i = 0; i < selectedTemplate.adset_count; i++) {
        const group = [];
        for (let j = 0; j < selectedTemplate.ad_per_adset; j++) {
          const messageIndex = j % messages.length;
          const headlineIndex = j % headlines.length;
          
          group.push({
            adset_index: i,
            ad_index: j,
            name: `${baseName}-${i + 1}-${j + 1}`,
            display_name: `广告${j + 1}`,
            message: messages[messageIndex] || '',
            messages: [messages[messageIndex] || ''],
            headline: headlines[headlineIndex] || '',
            headlines: [headlines[headlineIndex] || ''],
            descriptions: [],
            call_to_action: selectedTemplate.call_to_action || 'SEE_DETAILS',
            link_url: selectedTemplate.link_url || '',
            material_ids: [],
            status: 'pending',
            facebook_ad_id: null,
            creative_id: null,
          });
        }
        initialAds.push(group);
      }
      setAdsData(initialAds);

      const totalAds = selectedTemplate.adset_count * selectedTemplate.ad_per_adset;
      setProgress({ total: totalAds, completed: 0 });

      setCurrentStep(1);
      message.success('任务创建成功');
    } catch (error) {
      message.error('创建任务失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    if (!selectedTemplate) {
      message.error('请选择模板');
      return;
    }
    if (!selectedAccount) {
      message.error('请选择广告账户');
      return;
    }
    if (!selectedPixelId) {
      message.error('请选择像素（本系列全程使用同一个像素）');
      return;
    }
    if (!baseName.trim()) {
      message.error('请输入基础名称');
      return;
    }

    setLoading(true);
    let validation = null;
    try {
      validation = await checkAccountPrerequisites(selectedAccount);
      if (!validation.ok) {
        setAccountPrereqModal({ open: true, issues: validation.issues });
        return;
      }
      setAccountPages(validation.pages);
      setAccountPixels(validation.pixels);
      const pixelStillValid = validation.pixels.some((p) => p.pixel_id === selectedPixelId);
      if (!pixelStillValid) {
        message.error('所选像素不可用，请重新选择');
        if (validation.pixels[0]?.pixel_id) {
          setSelectedPixelId(validation.pixels[0].pixel_id);
        }
        return;
      }
    } finally {
      setLoading(false);
    }
    await executeCreateTask({
      defaultPixelId: selectedPixelId,
    });
  };

  // ========== 步骤 1: Campaign ==========
  const handleSaveCampaign = async () => {
    if (!taskId) return;
    if (!selectedAccount) {
      message.error('请先选择广告账户');
      return;
    }

    // 检查是否已有 Campaign 创建完成，防止重复提交
    if (campaignData.facebook_campaign_id || currentStep > 1) {
      Modal.confirm({
        title: '确认重新创建',
        content: 'Campaign 已创建完成，重新创建可能导致重复。是否继续？',
        okText: '继续',
        cancelText: '取消',
        onOk: async () => {
          await executeSaveCampaign();
        }
      });
      return;
    }

    await executeSaveCampaign();
  };

  const executeSaveCampaign = async () => {
    setLoading(true);
    try {
      // 1. 保存到数据库
      const campaignParams = buildCampaignParamsForMode({
        creationMode,
        seriesMode: getEffectiveSeriesMode(selectedTemplate, seriesMode),
        budgetMode,
        campaignData,
        selectedTemplate,
      });
      if (selectedAdvertiserIdentityId != null && selectedAdvertiserIdentityId !== '') {
        campaignParams.advertiser_identity_id = selectedAdvertiserIdentityId;
      }

      const campaignRes = await api.post(`/ad-creation-tasks/${taskId}/campaign`, {
        task_id: taskId,
        campaign_name: campaignData.name,
        campaign_display_name: campaignData.display_name,
        campaign_params: campaignParams,
        ad_account_id: selectedAccount,
      });

      const materialSync = campaignRes.data?.material_sync;
      if (materialSync) {
        if (materialSync.total_materials > 0) {
          if (materialSync.success) {
            setMaterialsUploaded(true);
            const upload = materialSync.upload_result || {};
            const reused = materialSync.reused_count ?? upload.reused_count ?? 0;
            const viaUrl = materialSync.shared_via_url_count ?? upload.shared_via_url_count ?? 0;
            const copyCnt = materialSync.shared_copy_count ?? upload.shared_copy_count ?? 0;
            const chunked = materialSync.uploaded_chunked_count ?? upload.uploaded_chunked_count ?? 0;
            if (materialSync.need_upload_count > 0 || viaUrl > 0 || chunked > 0 || copyCnt > 0) {
              const parts = [];
              if (reused > 0) parts.push(`复用 ${reused}`);
              if (viaUrl > 0) parts.push(`视频拉取 ${viaUrl}`);
              if (copyCnt > 0) parts.push(`图片分享 ${copyCnt}`);
              if (chunked > 0) parts.push(`全量上传 ${chunked}`);
              message.success(
                parts.length
                  ? `素材已同步：${parts.join('，')}`
                  : `素材已同步：成功 ${upload.success_count ?? 0}`
              );
            } else {
              message.success(
                reused > 0
                  ? `所有素材已在广告账户中（复用 ${reused}），无需上传`
                  : '所有素材已在广告账户中，无需上传'
              );
            }
          } else {
            setMaterialsUploaded(false);
            const uploadErr = materialSync.upload_result?.error
              || materialSync.upload_result?.errors?.[0]?.error
              || materialSync.error
              || materialSync.message;
            message.error('素材上传失败: ' + (uploadErr || '请检查后重试'));
            return;
          }
        }
      }

      // 2. 调用 Facebook API 创建 Campaign
      const execRes = await api.post(`/ad-creation-tasks/${taskId}/execute-campaign?ad_account_id=${selectedAccount}`);
      if (execRes.data.success) {
        message.success(`Campaign 创建成功: ${execRes.data.campaign_id}`);
        
        // 从后端获取广告数据（包含文案库数据）
        const taskRes = await api.get(`/ad-creation-tasks/${taskId}`);
        if (taskRes.data) {
          setTask(taskRes.data);
        }
        if (taskRes.data.ads && taskRes.data.ads.length > 0) {
          // 将扁平化的 ads 数组转换为二维数组（group by adset_index）
          const adsByAdset = {};
          taskRes.data.ads.forEach(ad => {
            if (!adsByAdset[ad.adset_index]) {
              adsByAdset[ad.adset_index] = [];
            }
            
            // 解析 params（可能是字符串或对象）
            let params = ad.params;
            if (typeof params === 'string') {
              try {
                params = JSON.parse(params);
              } catch (e) {
                params = {};
              }
            }
            
            adsByAdset[ad.adset_index].push({
              ...ad,
              messages: params?.messages || [ad.message || ''],
              headlines: params?.headlines || [ad.headline || ''],
              descriptions: params?.descriptions || []
            });
          });
          setAdsData(Object.values(adsByAdset));
        }
        
        setCurrentStep(2);
      } else {
        message.error('Facebook API 创建 Campaign 失败: ' + (execRes.data.error || '未知错误'));
      }
    } catch (error) {
      message.error('保存 Campaign 失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // ========== 步骤 2: AdSets ==========
  const isAdsetCreated = (adset) => adset?.status === 'done' && !!adset?.facebook_adset_id;

  const areAllAdsetsCreated = (adsets) => adsets.length > 0 && adsets.every(isAdsetCreated);

  const getIncompleteAdsetIndexes = (adsets) =>
    adsets.reduce((indexes, adset, index) => {
      if (!isAdsetCreated(adset)) {
        indexes.push(index);
      }
      return indexes;
    }, []);

  const showIncompleteAdsetWarning = (adsets) => {
    const indexes = getIncompleteAdsetIndexes(adsets);
    if (indexes.length === 0) {
      return false;
    }
    message.error(`还有 ${indexes.length} 个广告组未创建成功，请处理后重试`);
    indexes.forEach((index) => {
      const adset = adsets[index];
      const detail = adset?.error_message || (adset?.status === 'failed' ? '创建失败' : '未创建');
      message.warning(`第 ${index + 1} 组: ${detail}`);
    });
    return true;
  };

  const proceedToAdCreationStep = async () => {
    await api.post(`/ad-creation-tasks/${taskId}/adsets/complete`);

    if (materialsUploaded) {
      setCurrentStep(3);
      message.success('所有 AdSet 创建完成，进入广告创建步骤');
      return;
    }
    if (hasAdMaterialsHash()) {
      setMaterialsUploaded(true);
      setCurrentStep(3);
      message.success('所有 AdSet 创建完成，进入广告创建步骤');
      return;
    }

    message.loading('正在匹配素材...', 0);
    const materialsResult = await matchAllAdMaterials();
    message.destroy();

    if (materialsResult) {
      if (materialsResult.need_upload?.length > 0) {
        message.loading('正在上传素材...', 0);
        await batchUploadMaterials(materialsResult.need_upload);
        message.destroy();
      } else {
        setMaterialsUploaded(true);
        message.success('所有素材已上传，可进行广告创建');
      }
    }

    setCurrentStep(3);
    message.success('所有 AdSet 创建完成，进入广告创建步骤');
  };

  const handleSaveAdset = async (adsetIndex) => {
    if (!taskId) return;
    if (!selectedAccount) {
      message.error('请先选择广告账户');
      return;
    }

    const adset = adsetsData[adsetIndex];
    
    // 验证系列像素
    if (!selectedPixelId && !adset.pixel_id) {
      message.error('请先在创建任务时选择像素');
      return;
    }
    
    setLoading(true);
    try {
      // 获取模板配置作为基础
      const templateCfg = selectedTemplate?.adset_configs?.[Math.min(adsetIndex, (selectedTemplate?.adset_configs?.length || 1) - 1)] || {};
      
      // 处理 targeting 中的 locales（转换为对象数组格式）
      const processedTargeting = { ...adset.targeting };
      if (processedTargeting.locales && Array.isArray(processedTargeting.locales)) {
        processedTargeting.locales = processedTargeting.locales.map(l => ({
          id: l.value || l,
          name: l.label || l.value || l
        }));
      }
      
      // 1. 保存到数据库（合并模板配置和用户修改）
      const adsetParams = {
          name: adset.name,
          optimization_goal: adset.optimization_goal,
          billing_event: adset.billing_event,
          targeting: processedTargeting,
          placements: templateCfg.placements || {},
          status: 'ACTIVE',
          conversion_location: adset.conversion_location,
          performance_goal: adset.performance_goal,
          pixel_id: selectedPixelId || adset.pixel_id,
          conversion_event: adset.conversion_event,
          attribution_setting: adset.attribution_setting,
          advantage_audience: adset.advantage_audience ?? 0,
          device_targeting: adset.device_targeting,
          start_time: templateCfg.start_time,
          end_time: templateCfg.end_time,
        };
      if (selectedAdvertiserIdentityId != null && selectedAdvertiserIdentityId !== '') {
        adsetParams.advertiser_identity_id = selectedAdvertiserIdentityId;
      }

      if (creationMode !== CREATION_MODES.CBO_REGULAR_BID) {
        adsetParams.daily_budget = adset.daily_budget;
        adsetParams.lifetime_budget = adset.lifetime_budget;
      }
      if (creationMode === CREATION_MODES.BUILD_PLAN) {
        adsetParams.bid_amount = adset.bid_amount;
        adsetParams.bid_strategy = adset.bid_strategy;
      } else if (
        creationMode === CREATION_MODES.ABO_REGULAR_BID ||
        creationMode === CREATION_MODES.CBO_REGULAR_BID
      ) {
        if (seriesMode !== SERIES_MODES.ROI) {
          adsetParams.bid_amount = adset.bid_amount ?? selectedTemplate?.bid_amount;
        }
      }
      adsetParams.series_mode = getEffectiveSeriesMode(selectedTemplate, seriesMode);
      if (seriesMode === SERIES_MODES.ROI) {
        adsetParams.roi_coefficient =
          adset.roi_coefficient != null ? Number(adset.roi_coefficient) : 1.5;
        adsetParams.optimization_goal = 'VALUE';
        adsetParams.performance_goal = 'MAXIMIZE_CONVERSION_VALUE';
        adsetParams.billing_event = 'IMPRESSIONS';
      }

      await api.post(`/ad-creation-tasks/${taskId}/adsets`, {
        task_id: taskId,
        adset_index: adsetIndex,
        name: adset.name,
        display_name: adset.display_name,
        params: adsetParams,
        schedule: {
          start_time: adset.schedule && adset.schedule[0] ? adset.schedule[0].format() : null,
          end_time: adset.schedule && adset.schedule[1] ? adset.schedule[1].format() : null
        }
      });

      // 2. 调用 Facebook API 创建 AdSet
      // 检查该组下的广告是否使用多版本（动态素材）
      // 优先使用用户手动设置的值，如果没有设置则自动检测
      let hasDynamicCreative = adset.use_dynamic_creative;
      if (hasDynamicCreative === undefined || hasDynamicCreative === null) {
        const groupAds = adsData[adsetIndex] || [];
        hasDynamicCreative = groupAds.some(ad => {
          const msgs = ad.messages || [ad.message || ''];
          const hdls = ad.headlines || [ad.headline || ''];
          const descs = ad.descriptions || [];
          return msgs.length > 1 || hdls.length > 1 || descs.length > 1;
        });
      }
      
      const execRes = await api.post(
        `/ad-creation-tasks/${taskId}/execute-adset/${adsetIndex}?ad_account_id=${selectedAccount}&use_dynamic_creative=${hasDynamicCreative}`
      );
      if (execRes.data.success) {
        message.success(`AdSet ${adsetIndex + 1} 创建成功`);
        const newAdsets = [...adsetsData];
        newAdsets[adsetIndex].facebook_adset_id = execRes.data.adset_id;
        newAdsets[adsetIndex].status = 'done';
        newAdsets[adsetIndex].error_message = null;
        setAdsetsData(newAdsets);
      } else {
        const newAdsets = [...adsetsData];
        newAdsets[adsetIndex].status = 'failed';
        newAdsets[adsetIndex].error_message = execRes.data.error || '未知错误';
        setAdsetsData(newAdsets);
        message.error(`AdSet ${adsetIndex + 1} 创建失败: ` + (execRes.data.error || '未知错误'));
      }
    } catch (error) {
      message.error('保存 AdSet 失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 匹配所有广告的素材
  const matchAllAdMaterials = async () => {
    if (!taskId || !selectedAccount) return;
    
    setMatchingMaterials(true);
    
    try {
      // 调用后端接口匹配素材
      const response = await api.post(`/ad-creation-tasks/${taskId}/ads/match-materials`, {
        ad_account_id: selectedAccount
      });
      
      return response.data;
    } catch (error) {
      message.error('匹配素材失败: ' + (error.response?.data?.detail || error.message));
      return null;
    } finally {
      setMatchingMaterials(false);
    }
  };
  
  // 批量上传素材
  const batchUploadMaterials = async (materialsToUpload) => {
    if (!materialsToUpload || materialsToUpload.length === 0) {
      setMaterialsUploaded(true);
      return;
    }
    
    setUploadingMaterials(true);
    setMaterialUploadProgress(0);
    
    try {
      // 调用后端批量上传接口
      const response = await api.post(`/ad-creation-tasks/${taskId}/ads/upload-materials`, {
        ad_account_id: selectedAccount,
        material_ids: materialsToUpload
      }, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setMaterialUploadProgress(percentCompleted);
        }
      });
      
      if (response.data.success) {
        const parts = [];
        if (response.data.reused_count) parts.push(`复用 ${response.data.reused_count}`);
        if (response.data.shared_via_url_count) parts.push(`视频拉取 ${response.data.shared_via_url_count}`);
        if (response.data.shared_copy_count) parts.push(`图片分享 ${response.data.shared_copy_count}`);
        if (response.data.uploaded_chunked_count) parts.push(`全量上传 ${response.data.uploaded_chunked_count}`);
        message.success(
          parts.length
            ? `素材同步成功：${parts.join('，')}；失败 ${response.data.failed_count || 0}`
            : `素材上传成功！成功: ${response.data.success_count}, 失败: ${response.data.failed_count}`
        );
      } else {
        message.error('素材上传失败: ' + response.data.error);
      }
      
      setMaterialsUploaded(true);
    } catch (error) {
      message.error('素材上传失败: ' + (error.response?.data?.detail || error.message));
      setMaterialsUploaded(false);
    } finally {
      setUploadingMaterials(false);
      setMaterialUploadProgress(0);
    }
  };
  
  const handleCompleteAdsets = async () => {
    if (!taskId) return;

    if (!areAllAdsetsCreated(adsetsData)) {
      showIncompleteAdsetWarning(adsetsData);
      return;
    }

    setLoading(true);
    try {
      await proceedToAdCreationStep();
    } catch (error) {
      message.error('完成 AdSet 步骤失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // ========== 批量创建 AdSets ==========
  const handleSaveAllAdsets = async () => {
    if (!taskId) return;
    if (!selectedAccount) {
      message.error('请先选择广告账户');
      return;
    }

    // 检查是否已有 AdSet 创建完成，防止重复提交
    const hasCreatedAdsets = adsetsData.some(adset => adset.status === 'done' || adset.facebook_adset_id);
    if (hasCreatedAdsets) {
      Modal.confirm({
        title: '确认重新创建',
        content: '部分 AdSet 已创建完成，重新创建可能导致重复。是否继续？',
        okText: '继续',
        cancelText: '取消',
        onOk: async () => {
          await executeSaveAllAdsets();
        }
      });
      return;
    }

    await executeSaveAllAdsets();
  };

  const executeSaveAllAdsets = async () => {
    setLoading(true);
    try {
      // 验证系列像素
      if (!selectedPixelId) {
        message.error('请先在创建任务时选择像素');
        setLoading(false);
        return;
      }
      // 统一写入各 AdSet
      const syncedAdsets = adsetsData.map((adset) => ({
        ...adset,
        pixel_id: selectedPixelId,
        advertiser_identity_id:
          selectedAdvertiserIdentityId != null && selectedAdvertiserIdentityId !== ''
            ? selectedAdvertiserIdentityId
            : null,
      }));
      if (syncedAdsets.some((a, i) => (
        a.pixel_id !== adsetsData[i].pixel_id
        || a.advertiser_identity_id !== adsetsData[i].advertiser_identity_id
      ))) {
        setAdsetsData(syncedAdsets);
      }

      if (creationMode !== CREATION_MODES.CBO_REGULAR_BID && budgetMode === 'ABO') {
        const adsetWithoutBudget = adsetsData.find(
          (adset) => !adset.daily_budget && !adset.lifetime_budget
        );
        if (adsetWithoutBudget) {
          const index = adsetsData.indexOf(adsetWithoutBudget);
          message.error(`第 ${index + 1} 组请填写每日预算或总预算（ABO 模式）`);
          setLoading(false);
          return;
        }
      }

      if (seriesMode === SERIES_MODES.ROI) {
        const adsetWithoutRoi = adsetsData.find(
          (adset) => adset.roi_coefficient == null || !(Number(adset.roi_coefficient) > 0)
        );
        if (adsetWithoutRoi) {
          const index = adsetsData.indexOf(adsetWithoutRoi);
          message.error(`第 ${index + 1} 组请填写有效的 ROI 系数（默认 1.5）`);
          setLoading(false);
          return;
        }
      }
      
      // 构建批量请求数据
      const adsetDataList = adsetsData.map((adset, index) => {
        const params = {
          optimization_goal: adset.optimization_goal,
          use_dynamic_creative: adset.use_dynamic_creative,
          conversion_location: adset.conversion_location,
          performance_goal: adset.performance_goal,
          pixel_id: selectedPixelId || adset.pixel_id,
          conversion_event: adset.conversion_event,
          attribution_setting: adset.attribution_setting,
          advantage_audience: adset.advantage_audience ?? 0,
          device_targeting: adset.device_targeting,
          targeting: adset.targeting,
          billing_event: adset.billing_event,
        };
        if (selectedAdvertiserIdentityId != null && selectedAdvertiserIdentityId !== '') {
          params.advertiser_identity_id = selectedAdvertiserIdentityId;
        }
        if (creationMode !== CREATION_MODES.CBO_REGULAR_BID) {
          params.daily_budget = adset.daily_budget;
          params.lifetime_budget = adset.lifetime_budget;
        }
        if (creationMode === CREATION_MODES.BUILD_PLAN) {
          params.bid_amount = adset.bid_amount;
          params.bid_strategy = adset.bid_strategy;
        } else if (
          creationMode === CREATION_MODES.ABO_REGULAR_BID ||
          creationMode === CREATION_MODES.CBO_REGULAR_BID
        ) {
          if (seriesMode !== SERIES_MODES.ROI) {
            params.bid_amount = adset.bid_amount ?? selectedTemplate?.bid_amount;
          }
        }
        params.series_mode = getEffectiveSeriesMode(selectedTemplate, seriesMode);
        if (seriesMode === SERIES_MODES.ROI) {
          params.roi_coefficient =
            adset.roi_coefficient != null ? Number(adset.roi_coefficient) : 1.5;
          params.optimization_goal = 'VALUE';
          params.performance_goal = 'MAXIMIZE_CONVERSION_VALUE';
          params.billing_event = 'IMPRESSIONS';
        }
        return {
          adset_index: index,
          name: adset.name,
          display_name: adset.display_name,
          params,
          schedule: adset.schedule ? {
            start_time: adset.schedule[0]?.format('YYYY-MM-DD HH:mm:ss'),
            end_time: adset.schedule[1]?.format('YYYY-MM-DD HH:mm:ss')
          } : null
        };
      });

      console.log('[DEBUG] Batch Adset Payload:', adsetDataList);

      // 调用批量创建API（保存到数据库）
      const res = await api.post(`/ad-creation-tasks/${taskId}/adsets/batch`, {
        adsets: adsetDataList
      });

      console.log('[DEBUG] Batch Adset Response:', res.data);

      if (res.data.success) {
        message.success(`批量保存成功！成功: ${res.data.success_count} / 总数: ${res.data.total_count}`);
        
        // 如果有失败的，显示失败信息
        if (res.data.failed_count > 0) {
          const failedItems = res.data.results.filter(r => !r.success);
          failedItems.forEach(item => {
            console.error(`AdSet ${item.adset_index} 保存失败:`, item.message);
          });
        }

        // 调用后端批量执行 API，一次性创建所有 AdSets
        const execRes = await api.post(`/ad-creation-tasks/${taskId}/execute-adsets`, {
          ad_account_id: selectedAccount
        });

        console.log('[DEBUG] Execute Adsets Response:', execRes.data);

        if (execRes.data.results?.length) {
          const newAdsets = [...adsetsData];
          const pendingIndexes = newAdsets
            .map((adset, index) => (!isAdsetCreated(adset) ? index : -1))
            .filter((index) => index >= 0);

          execRes.data.results.forEach((result, resultIndex) => {
            const adsetIndex = pendingIndexes[resultIndex];
            if (adsetIndex === undefined) {
              return;
            }
            if (result.success) {
              newAdsets[adsetIndex].facebook_adset_id = result.data?.id;
              newAdsets[adsetIndex].status = 'done';
              newAdsets[adsetIndex].error_message = null;
            } else {
              newAdsets[adsetIndex].status = 'failed';
              newAdsets[adsetIndex].error_message = result.error || result.message || '创建失败';
            }
          });
          setAdsetsData(newAdsets);

          const successCount = execRes.data.success_count || 0;
          const failedCount = execRes.data.failed_count || 0;
          if (execRes.data.success) {
            message.success(`Facebook AdSet 创建完成！成功: ${successCount} / 总数: ${execRes.data.total_count}`);
          } else {
            message.error(`Facebook AdSet 部分创建失败：成功 ${successCount}，失败 ${failedCount}`);
          }

          if (areAllAdsetsCreated(newAdsets)) {
            await proceedToAdCreationStep();
          } else {
            showIncompleteAdsetWarning(newAdsets);
          }
        } else if (execRes.data.success) {
          message.success(`Facebook AdSet 创建完成！成功: ${execRes.data.success_count} / 总数: ${execRes.data.total_count}`);
          if (areAllAdsetsCreated(adsetsData)) {
            await proceedToAdCreationStep();
          } else {
            showIncompleteAdsetWarning(adsetsData);
          }
        } else {
          message.error('批量执行 AdSet 创建失败: ' + (execRes.data.error || 'Unknown error'));
        }
      } else {
        message.error('批量保存失败');
      }
    } catch (error) {
      message.error('批量创建 AdSet 失败: ' + (error.response?.data?.detail || error.message));
      console.error('Batch Adset Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ========== 步骤 3: Ads ==========
  const isAdCreated = (ad) => ad?.status === 'done' && !!(ad?.facebook_ad_id || ad?.ad_id);

  const areAllAdsCreated = () =>
    adsData.length > 0 && adsData.every((group) => group.every(isAdCreated));

  const getIncompleteAdIndexes = () => {
    const incomplete = [];
    adsData.forEach((group, groupIndex) => {
      group.forEach((ad, adIndex) => {
        if (!isAdCreated(ad)) {
          incomplete.push({ groupIndex, adIndex, ad });
        }
      });
    });
    return incomplete;
  };

  const showIncompleteAdsWarning = () => {
    const incomplete = getIncompleteAdIndexes();
    if (incomplete.length === 0) {
      return false;
    }
    message.error(`还有 ${incomplete.length} 个广告未创建成功，请先完成创建`);
    incomplete.forEach(({ groupIndex, adIndex, ad }) => {
      const detail = ad?.error_message || (ad?.status === 'failed' ? '创建失败' : '未创建');
      message.warning(`组 ${groupIndex + 1} - 广告 ${adIndex + 1}: ${detail}`);
    });
    return true;
  };

  const handleCompleteAds = async () => {
    if (!taskId) return;

    if (!areAllAdsCreated()) {
      showIncompleteAdsWarning();
      return;
    }

    setLoading(true);
    try {
      await api.post(`/ad-creation-tasks/${taskId}/ads/complete`);
      taskCompletedRef.current = true;
      draftTaskLoadedRef.current = null;
      message.success('所有广告创建完成！');
      navigate('/campaigns', { replace: true });
    } catch (error) {
      message.error('更新任务状态失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAd = async (adsetIndex, adIndex) => {
    if (!taskId) return;
    if (!selectedAccount) {
      message.error('请先选择广告账户');
      return;
    }

    const ad = adsData[adsetIndex][adIndex];
    const allowedSet = new Set(
      accountPages.map((p) => String(p.page_id || p.id))
    );
    const effectivePageId = ad.page_id || pageId;
    if (!isAdPageLocked(ad) && !effectivePageId) {
      message.error('请先选择可分配的公共主页');
      return;
    }
    if (!isAdPageLocked(ad) && effectivePageId && !allowedSet.has(String(effectivePageId))) {
      message.error('当前公共主页不可用（可能已暂停），正在重新分配…');
      await reconcilePagesForStep3(selectedAccount, taskId, adsData);
      return;
    }

    setLoading(true);
    try {
      // 1. 保存到数据库
      const saveAdPayload = {
        task_id: taskId,
        adset_index: adsetIndex,
        ad_index: adIndex,
        name: ad.name,
        display_name: ad.display_name,
        message: ad.message,
        messages: ad.messages || [ad.message || ''],
        headline: ad.headline,
        headlines: ad.headlines || [ad.headline || ''],
        descriptions: ad.descriptions || [],
        call_to_action: ad.call_to_action,
        link_url: ad.link_url,
        material_ids: ad.material_ids,
        page_id: ad.page_id || null  // 新增字段
      };
      console.log('[DEBUG] Step 3 - Save Ad Payload:', saveAdPayload);
      
      await api.post(`/ad-creation-tasks/${taskId}/ads`, saveAdPayload);

      // 2. 调用 Facebook API 创建广告
      const pageForExecute = ad.page_id || pageId;
      const executeAdUrl = `/ad-creation-tasks/${taskId}/execute-ad/${adsetIndex}/${adIndex}?ad_account_id=${selectedAccount}&page_id=${pageForExecute}`;
      console.log('[DEBUG] Step 3 - Execute Ad URL:', executeAdUrl);
      const execRes = await api.post(executeAdUrl);
      if (execRes.data.success) {
        message.success(`广告 ${adsetIndex + 1}-${adIndex + 1} 创建成功`);
        const newAdsData = [...adsData];
        newAdsData[adsetIndex][adIndex].status = 'done';
        newAdsData[adsetIndex][adIndex].facebook_ad_id = execRes.data.ad_id;
        newAdsData[adsetIndex][adIndex].creative_id = execRes.data.creative_id;
        setAdsData(newAdsData);
        setProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
        // 清除该广告的错误信息
        setErrorInfo(prev => prev.filter(e => e.adset_index !== adsetIndex || e.ad_index !== adIndex));
      } else {
        const errorMsg = execRes.data.error || '未知错误';
        message.error('Facebook 创建广告失败: ' + errorMsg);
        // 记录失败信息
        setErrorInfo(prev => [...prev, {
          adset_index: adsetIndex,
          ad_index: adIndex,
          error: errorMsg
        }]);
        // 更新广告状态为失败
        const newAdsData = [...adsData];
        newAdsData[adsetIndex][adIndex].status = 'failed';
        setAdsData(newAdsData);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.message || '未知错误';
      message.error('保存广告失败: ' + errorMsg);
      // 记录失败信息
      setErrorInfo(prev => [...prev, {
        adset_index: adsetIndex,
        ad_index: adIndex,
        error: errorMsg
      }]);
      // 更新广告状态为失败
      const newAdsData = [...adsData];
      newAdsData[adsetIndex][adIndex].status = 'failed';
      setAdsData(newAdsData);
    } finally {
      setLoading(false);
    }
  };

  // ========== 批量创建广告（分批处理）==========
  const handleSaveAllAds = async () => {
    if (!taskId) return;
    if (!selectedAccount) {
      message.error('请先选择广告账户');
      return;
    }
    if (!pageId) {
      message.error('请输入 Facebook 主页 ID');
      return;
    }

    // 检查是否已有广告创建完成，防止重复提交
    const hasCreatedAds = adsData.some(group => group.some(ad => ad.facebook_ad_id));
    if (hasCreatedAds) {
      Modal.confirm({
        title: '确认重新创建',
        content: '部分广告已创建完成，重新创建可能导致重复广告。是否继续？',
        okText: '继续',
        cancelText: '取消',
        onOk: async () => {
          await executeSaveAllAds();
        }
      });
      return;
    }

    await executeSaveAllAds();
  };

  const executeSaveAllAds = async () => {
    setLoading(true);
    // 清空之前的错误信息
    setErrorInfo([]);
    
    try {
      // 构建批量请求数据
      const adDataList = adsData.flatMap((group, adsetIndex) =>
        group.map((ad, adIndex) => ({
          adset_index: adsetIndex,
          ad_index: adIndex,
          name: ad.name,
          display_name: ad.display_name,
          message: ad.message,
          messages: ad.messages || [ad.message || ''],
          headline: ad.headline,
          headlines: ad.headlines || [ad.headline || ''],
          descriptions: ad.descriptions || [],
          call_to_action: ad.call_to_action,
          link_url: ad.link_url,
          material_ids: ad.material_ids || [],
          page_id: ad.page_id || null
        }))
      );

      console.log('[DEBUG] Batch Ad Payload:', adDataList);

      // 调用批量创建API（保存到数据库）
      const res = await api.post(`/ad-creation-tasks/${taskId}/ads/batch`, {
        ads: adDataList
      }, {
        timeout: 60000  // 超时时间设为60秒
      });

      console.log('[DEBUG] Batch Ad Response:', res.data);

      if (res.data.success) {
        message.success(`批量保存成功！成功: ${res.data.success_count} / 总数: ${res.data.total_count}`);
        
        // 如果有失败的，显示失败信息
        if (res.data.failed_count > 0) {
          const failedItems = res.data.results.filter(r => !r.success);
          const errorMessages = failedItems.map(item => ({
            adset_index: item.adset_index,
            ad_index: item.ad_index,
            error: item.message
          }));
          setErrorInfo(prev => [...prev, ...errorMessages]);
          failedItems.forEach(item => {
            console.error(`Ad ${item.adset_index}-${item.ad_index} 创建失败:`, item.message);
          });
        }

        // ========== 调用后端批量创建广告（统一由后端处理分批） ==========
        try {
          console.log('[DEBUG] 开始批量创建广告');
          
          // 调用后端接口一次处理所有广告
          const execRes = await api.post(`/ad-creation-tasks/${taskId}/execute-ads`, {
            ad_account_id: selectedAccount,
            page_id: pageId
          }, {
            timeout: 600000  // 超时时间设为10分钟，给后端足够时间处理所有广告
          });
          
          console.log('[DEBUG] 批量创建响应:', execRes.data);
          
          if (execRes.data.results?.length > 0) {
            // 更新广告状态
            let localAdsData = [...adsData];
            const newErrors = [];
            
            for (let i = 0; i < execRes.data.results.length; i++) {
              const result = execRes.data.results[i];
              if (result.adset_index !== undefined && result.ad_index !== undefined) {
                const { adset_index, ad_index } = result;
                if (adset_index < localAdsData.length && ad_index < localAdsData[adset_index].length) {
                  if (result.success) {
                    localAdsData[adset_index][ad_index].status = 'done';
                    localAdsData[adset_index][ad_index].facebook_ad_id = result.data.id;
                    localAdsData[adset_index][ad_index].creative_id = result.data.creative_id;
                  } else {
                    newErrors.push({
                      adset_index: adset_index,
                      ad_index: ad_index,
                      error: parseErrorMessage(result.error)
                    });
                    localAdsData[adset_index][ad_index].status = 'failed';
                  }
                }
              }
            }
            
            setAdsData([...localAdsData]);
            if (newErrors.length > 0) {
              setErrorInfo(prev => [...prev, ...newErrors]);
            }
            
            const successCount = execRes.data.success_count || 0;
            const failedCount = execRes.data.failed_count || 0;
            message.success(`广告批量创建完成! 成功: ${successCount}, 失败: ${failedCount}`);
            if (successCount > 0 && failedCount === 0) {
              message.info('全部广告已创建成功，请点击「完成」结束任务');
            }
          } else if (!execRes.data.success) {
            const errorMsg = execRes.data.error || '批量创建失败';
            message.error('Facebook 批量创建广告失败: ' + parseErrorMessage(errorMsg));
          }
        } catch (err) {
          console.error('Batch execute ads error:', err);
          const errorMsg =
            err.response?.data?.detail ||
            err.response?.data?.error ||
            err.message ||
            '未知错误';
          message.error('批量创建广告失败: ' + parseErrorMessage(errorMsg));
        }
      } else {
        message.error('批量保存失败');
      }
    } catch (error) {
      message.error('批量保存广告失败: ' + (error.response?.data?.detail || error.message));
      console.error('Batch Ad Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ========== 错误信息解析 ==========
  const parseErrorMessage = (error) => {
    if (!error) return '未知错误';
    
    // 尝试解析字符串化的 JSON 错误信息
    try {
      // 检查是否是字符串化的错误对象
      if (typeof error === 'string' && (error.startsWith('{') || error.startsWith("{'"))) {
        // 处理单引号转义
        const normalizedError = error.replace(/'/g, '"');
        const parsed = JSON.parse(normalizedError);
        
        // 提取错误信息
        if (parsed.error) {
          if (typeof parsed.error === 'string') {
            return parsed.error;
          } else if (parsed.error.message) {
            return parsed.error.message;
          } else if (parsed.error.error_user_msg) {
            return parsed.error.error_user_msg;
          } else {
            return JSON.stringify(parsed.error);
          }
        }
        
        return JSON.stringify(parsed);
      }
    } catch (e) {
      console.error('Error parsing error message:', e);
    }
    
    return error;
  };

  // ========== 渲染 ==========
  const renderStep0 = () => (
    <Card title="选择模板和设置基础信息">
      <Form layout="vertical">
        <Form.Item label="选择模板" required>
          <Select
            placeholder="请选择模板"
            onChange={(value) => {
              const template = templates.find((t) => t.id === value);
              setSelectedTemplate(template || null);
              if (template?.budget_mode) {
                setBudgetMode(template.budget_mode);
              }
              setCreationMode(template?.creation_mode || CREATION_MODES.BUILD_PLAN);
              setSeriesMode(template?.series_mode || SERIES_MODES.REGULAR);
            }}
          >
            {templates.map(template => (
              <Option key={template.id} value={template.id}>
                {template.name} ({template.adset_count}组 × {template.ad_per_adset}广告)
              </Option>
            ))}
          </Select>
        </Form.Item>

        {selectedTemplate && (
          <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
            <p><strong>模板详情:</strong></p>
            <p>广告目标: {selectedTemplate.objective}</p>
            <p>创建模式: {getCreationModeLabel(selectedTemplate.creation_mode)}</p>
            <p>系列模式: {getSeriesModeLabel(selectedTemplate.series_mode || SERIES_MODES.REGULAR)}</p>
            <p>预算模式: {selectedTemplate.budget_mode}</p>
            <p>组数量: {selectedTemplate.adset_count}</p>
            <p>每组广告数: {selectedTemplate.ad_per_adset}</p>
            {selectedTemplate.body_count && (
              <p>文案配置: 正文{selectedTemplate.body_count}个 × 标题{selectedTemplate.headline_count}个 × 描述{selectedTemplate.description_count}个</p>
            )}
          </div>
        )}

        <Form.Item label="文案库（可选）">
          <Select 
            placeholder="请选择文案库（可选，选择后将使用该库的文案）" 
            value={selectedCreativeCopy?.id} 
            onChange={handleCreativeCopySelect}
            showSearch
            filterOption={false}
            onSearch={handleCreativeCopySearch}
            onPopupScroll={handleCreativeCopyPopupScroll}
            loading={creativeCopyMeta.loading && creativeCopies.length === 0}
            notFoundContent={
              creativeCopyMeta.loading ? '加载中...' : '暂无文案库'
            }
          >
            <Option value="">不使用文案库</Option>
            {creativeCopies.map(copy => (
              <Option key={copy.id} value={copy.id}>
                {copy.book_name}
                {copy.book_id ? ` (${copy.book_id})` : ''}
              </Option>
            ))}
            {creativeCopyMeta.loading && creativeCopies.length > 0 && (
              <Option key="__loading__" value="__loading__" disabled>
                加载中...
              </Option>
            )}
            {!creativeCopyMeta.hasMore && creativeCopies.length > 0 && (
              <Option key="__end__" value="__end__" disabled>
                已加载全部 {creativeCopyMeta.total} 条
              </Option>
            )}
          </Select>
        </Form.Item>

        {selectedCreativeCopy && (
          <div style={{ marginBottom: 16, padding: 12, background: '#e6f7ff', borderRadius: 8 }}>
            <p><strong>文案库详情:</strong></p>
            <p>书名: {selectedCreativeCopy.book_name}</p>
            {selectedCreativeCopy.book_id && <p>书籍ID: {selectedCreativeCopy.book_id}</p>}
            {selectedCreativeCopy.promotion_link && <p>推广链接: {selectedCreativeCopy.promotion_link}</p>}
            <p>文案数量: {selectedCreativeCopy.bodies?.length || 0}个正文, {selectedCreativeCopy.headlines?.length || 0}个标题, {selectedCreativeCopy.descriptions?.length || 0}个描述</p>
          </div>
        )}

        <Form.Item label="素材集（可选）">
          <Select 
            placeholder="请选择素材集（可选，选择后将使用该集的素材）" 
            value={selectedMaterialCollection?.id} 
            onChange={(value) => {
              if (!value) {
                setSelectedMaterialCollection(null);
                return;
              }
              setSelectedMaterialCollection(
                materialCollections.find((c) => c.id === parseInt(value, 10))
              );
            }}
            showSearch
            optionFilterProp="label"
            onPopupScroll={handleMaterialCollectionPopupScroll}
            loading={materialCollectionMeta.loading && materialCollections.length === 0}
            notFoundContent={
              materialCollectionMeta.loading ? '加载中...' : '暂无素材集'
            }
            options={[
              { value: '', label: '不使用素材集' },
              ...materialCollections.map((collection) => ({
                value: collection.id,
                label: `${collection.name} (${collection.total_materials || 0}个素材)`,
              })),
              ...(materialCollectionMeta.loading && materialCollections.length > 0
                ? [{ value: '__loading__', label: '加载中...', disabled: true }]
                : []),
              ...(!materialCollectionMeta.hasMore && materialCollections.length > 0
                ? [{
                    value: '__end__',
                    label: `已加载全部 ${materialCollectionMeta.total} 条`,
                    disabled: true,
                  }]
                : []),
            ]}
          />
        </Form.Item>

        {selectedMaterialCollection && (
          <div style={{ marginBottom: 16, padding: 12, background: '#f6ffed', borderRadius: 8 }}>
            <p><strong>素材集详情:</strong></p>
            <p>名称: {selectedMaterialCollection.name}</p>
            {selectedMaterialCollection.description && <p>描述: {selectedMaterialCollection.description}</p>}
            <p>素材数量: {selectedMaterialCollection.total_materials || selectedMaterialCollection.material_count || 0}个</p>
            {selectedAccount ? (
              <>
                <p>
                  账户 <strong>{selectedAccount}</strong> 下次将从第{' '}
                  <strong>
                    {materialAllocationPreview?.next_material_ordinal ??
                      (materialAllocationPreview?.next_material_index ?? 0) + 1}
                  </strong>{' '}
                  个素材开始分配
                </p>
                {materialAllocationPreview?.range_end_ordinal != null && (
                  <p>
                    本系列预计使用素材序号{' '}
                    <strong>
                      {materialAllocationPreview.range_start_ordinal}–
                      {materialAllocationPreview.range_end_ordinal}
                    </strong>
                    {materialAllocationPreview.wraps ? '（超出部分将循环复用）' : ''}
                  </p>
                )}
              </>
            ) : (
              <p style={{ color: '#fa8c16' }}>请先选择广告账户以预览该账户下的素材轮询位置</p>
            )}
            <p style={{ color: '#999', fontSize: 12 }}>
              不同广告账户独立计数：A 账户用到 20 后，B 账户仍从 1 开始；再回到 A 则从 21 继续。
            </p>
          </div>
        )}

        <Form.Item label="广告账户" required>
          <Select 
            placeholder="请选择广告账户" 
            value={selectedAccount} 
            onChange={(value) => {
              setSelectedAccount(value);
              setSelectedPixelId('');
              setAccountPixels([]);
              setSelectedAdvertiserIdentityId(undefined);
              setAdvertiserIdentities([]);
              // 只有步骤1和2需要获取像素，步骤3不需要
              if (currentStep < 3) {
                fetchAccountPixels(value);
                fetchAdvertiserIdentities(value);
              } else {
                console.log('Current step is 3, skipping account pixels fetch');
              }
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              // 直接通过 value 找到对应的广告账户数据，进行更精确的搜索
              const account = adAccounts.find(a => a.account_id === option.value);
              if (!account) return false;
              
              // 搜索广告账户名称、账户 ID 和其他相关字段
              const searchText = input.toLowerCase();
              const nameMatch = account.name.toLowerCase().includes(searchText);
              const idMatch = account.account_id.toLowerCase().includes(searchText);
              
              return nameMatch || idMatch;
            }}
          >
            {adAccounts.map(account => (
              <Option key={account.account_id} value={account.account_id}>
                {account.name} ({account.account_id})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="像素（本系列共用）"
          required
          extra="创建后所有 AdSet 使用同一像素；步骤 2 顶部也可再改"
        >
          <Select
            placeholder={selectedAccount ? '请选择像素' : '请先选择广告账户'}
            value={selectedPixelId || undefined}
            onChange={(value) => setSelectedPixelId(value)}
            disabled={!selectedAccount}
            showSearch
            optionFilterProp="children"
            notFoundContent={selectedAccount ? '该账户暂无可用像素' : '请先选择广告账户'}
          >
            {accountPixels.map((pixel) => (
              <Option key={pixel.pixel_id} value={pixel.pixel_id}>
                {pixel.name ? `${pixel.name} (${pixel.pixel_id})` : pixel.pixel_id}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="广告主体（可选）"
          extra="用于澳大利亚等区域合规公示；不选则创建广告组时不传主体参数"
        >
          <Select
            allowClear
            placeholder={selectedAccount ? '可不选；选择已验证主体' : '请先选择广告账户'}
            value={selectedAdvertiserIdentityId}
            onChange={(value) => setSelectedAdvertiserIdentityId(value)}
            disabled={!selectedAccount}
            showSearch
            optionFilterProp="children"
            notFoundContent={selectedAccount ? '该 BM 暂无主体，请先在「主体管理」添加' : '请先选择广告账户'}
          >
            {advertiserIdentities.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.display_name}
                {item.is_default ? '（默认）' : ''}
                {` · ${item.identity_id}`}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="基础名称" required>
          <Input placeholder="例如：夏季大促" value={baseName} onChange={(e) => setBaseName(e.target.value)} />
        </Form.Item>

        <Form.Item label="预算模式" required>
          <Radio.Group value={budgetMode} onChange={(e) => setBudgetMode(e.target.value)}>
            <Radio.Button value="CBO">CBO</Radio.Button>
            <Radio.Button value="ABO">ABO</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Button type="primary" onClick={handleStart} loading={loading}>创建任务</Button>
      </Form>
    </Card>
  );

  const renderStep1 = () => (
    <Card title="步骤 1/3: 创建 Campaign">
      <Form layout="vertical">
        <Form.Item label="广告账户" required>
          <Select 
            placeholder="选择广告账户" 
            value={selectedAccount} 
            onChange={setSelectedAccount}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              // 直接通过 value 找到对应的广告账户数据，进行更精确的搜索
              const account = adAccounts.find(a => a.account_id === option.value);
              if (!account) return false;
              
              // 搜索广告账户名称、账户 ID 和其他相关字段
              const searchText = input.toLowerCase();
              const nameMatch = account.name.toLowerCase().includes(searchText);
              const idMatch = account.account_id.toLowerCase().includes(searchText);
              
              return nameMatch || idMatch;
            }}
          >
            {adAccounts.map(account => (
              <Option key={account.account_id} value={account.account_id}>
                {account.name} ({account.account_id})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Campaign名称">
          <Input value={campaignData.name} disabled />
        </Form.Item>

        <Form.Item label="显示名称">
          <Input value={campaignData.display_name} onChange={(e) => setCampaignData({...campaignData, display_name: e.target.value})} />
        </Form.Item>

        <Form.Item
          label="广告目标"
          extra={
            getEffectiveSeriesMode(selectedTemplate, seriesMode) === SERIES_MODES.ROI
              ? 'ROI 模式须使用「销售」目标（支持价值优化）'
              : undefined
          }
        >
          <Select
            value={
              getEffectiveSeriesMode(selectedTemplate, seriesMode) === SERIES_MODES.ROI
                ? (['OUTCOME_SALES', 'OUTCOME_APP_PROMOTION'].includes(campaignData.objective)
                  ? campaignData.objective
                  : 'OUTCOME_SALES')
                : campaignData.objective
            }
            disabled={getEffectiveSeriesMode(selectedTemplate, seriesMode) === SERIES_MODES.ROI}
            onChange={(value) => setCampaignData({...campaignData, objective: value})}
          >
            <Option value="OUTCOME_TRAFFIC">流量</Option>
            <Option value="OUTCOME_SALES">销售</Option>
            <Option value="OUTCOME_LEADS">潜在客户</Option>
            <Option value="OUTCOME_ENGAGEMENT">互动</Option>
            <Option value="OUTCOME_AWARENESS">品牌认知</Option>
            <Option value="OUTCOME_APP_PROMOTION">应用推广</Option>
          </Select>
        </Form.Item>

        <Form.Item label="购买类型">
          <Select value={campaignData.buying_type} onChange={(value) => setCampaignData({...campaignData, buying_type: value})}>
            <Option value="AUCTION">竞拍</Option>
            <Option value="RESERVED">预留</Option>
          </Select>
        </Form.Item>

        <Form.Item label="预算模式">
          <Radio.Group value={budgetMode} disabled>
            <Radio.Button value="CBO">CBO (Campaign预算优化)</Radio.Button>
            <Radio.Button value="ABO">ABO (AdSet预算)</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="系列模式"
          extra="跟随所选模板设置，不可在此步骤修改"
        >
          <Radio.Group
            value={getEffectiveSeriesMode(selectedTemplate, seriesMode)}
            disabled
          >
            <Radio.Button value={SERIES_MODES.REGULAR}>
              {getSeriesModeLabel(SERIES_MODES.REGULAR)}
            </Radio.Button>
            <Radio.Button value={SERIES_MODES.ROI}>
              {getSeriesModeLabel(SERIES_MODES.ROI)}
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        {budgetMode === 'CBO' && campaignData.daily_budget && (
          <Form.Item label="Campaign日预算">
            <Input value={campaignData.daily_budget} disabled />
          </Form.Item>
        )}

        {creationMode === CREATION_MODES.CBO_REGULAR_BID || getEffectiveSeriesMode(selectedTemplate, seriesMode) === SERIES_MODES.ROI ? (
          <>
            <Form.Item label="广告系列竞价策略">
              <Select
                value={
                  getEffectiveSeriesMode(selectedTemplate, seriesMode) === SERIES_MODES.ROI
                    ? (campaignData.bid_strategy || 'LOWEST_COST_WITH_MIN_ROAS')
                    : campaignData.bid_strategy
                }
                disabled={getEffectiveSeriesMode(selectedTemplate, seriesMode) === SERIES_MODES.ROI}
                onChange={(value) => setCampaignData({
                  ...campaignData,
                  bid_strategy: value,
                  bid_amount:
                    value === 'LOWEST_COST_WITHOUT_CAP'
                    || value === 'LOWEST_COST_WITH_MIN_ROAS'
                      ? null
                      : campaignData.bid_amount,
                })}
              >
                {getCampaignBidStrategiesForSeries(getEffectiveSeriesMode(selectedTemplate, seriesMode)).map((s) => (
                  <Option key={s.value} value={s.value}>{s.label}</Option>
                ))}
              </Select>
            </Form.Item>
            {getEffectiveSeriesMode(selectedTemplate, seriesMode) !== SERIES_MODES.ROI
              && campaignData.bid_strategy !== 'LOWEST_COST_WITHOUT_CAP'
              && campaignData.bid_strategy !== 'LOWEST_COST_WITH_MIN_ROAS' && (
              <Form.Item label={campaignData.bid_strategy === 'COST_CAP' ? '单次成效费用目标 (USD)' : '竞价上限 (USD)'}>
                <Input
                  type="number"
                  value={campaignData.bid_amount ?? ''}
                  onChange={(e) => setCampaignData({
                    ...campaignData,
                    bid_amount: parseFloat(e.target.value) || null,
                  })}
                />
              </Form.Item>
            )}
          </>
        ) : (
        <Form.Item label="出价策略">
          <Select value={campaignData.bid_strategy} onChange={(value) => setCampaignData({...campaignData, bid_strategy: value})}>
            <Option value="LOWEST_COST_WITHOUT_CAP">最低成本（无上限）</Option>
            <Option value="LOWEST_COST_WITH_BID_CAP">最低成本（有出价上限）</Option>
            <Option value="COST_CAP">成本上限</Option>
            <Option value="BID_CAP">出价上限</Option>
          </Select>
        </Form.Item>
        )}

        <Form.Item label="特殊广告类别">
          <Select mode="multiple" value={campaignData.special_ad_categories || []} onChange={(value) => setCampaignData({...campaignData, special_ad_categories: value})}>
            <Option value="NONE">无</Option>
            <Option value="EMPLOYMENT">招聘</Option>
            <Option value="HOUSING">住房</Option>
            <Option value="CREDIT">信贷</Option>
            <Option value="ISSUES_ELECTIONS_POLITICS">政治</Option>
          </Select>
        </Form.Item>

        <Divider />
        <Space>
          <Button onClick={() => setCurrentStep(0)}>上一步</Button>
          <Button type="primary" onClick={handleSaveCampaign} loading={loading}>保存并下一步</Button>
        </Space>
      </Form>
    </Card>
  );

  const renderStep2 = () => {
    const handleSeriesPixelChange = (value) => {
      setSelectedPixelId(value);
      if (adsetsData.length > 0) {
        setAdsetsData(adsetsData.map((adset) => ({ ...adset, pixel_id: value })));
      }
      message.success('已切换系列像素，所有组将使用新像素');
    };

    const handleSeriesIdentityChange = (value) => {
      setSelectedAdvertiserIdentityId(value);
      if (adsetsData.length > 0) {
        setAdsetsData(adsetsData.map((adset) => ({
          ...adset,
          advertiser_identity_id: value ?? null,
        })));
      }
      message.success(value ? '已切换系列广告主体' : '已清空系列广告主体');
    };

    return (
    <Card title="步骤 2/3: 创建 AdSets">
      <div style={{ marginBottom: 16, padding: '10px 12px', background: '#f5f5f5', borderRadius: 8 }}>
        <Row gutter={12} align="middle" style={{ marginBottom: 8 }}>
          <Col flex="none">
            <strong>系列像素：</strong>
          </Col>
          <Col flex="auto" style={{ maxWidth: 480 }}>
            <Select
              value={selectedPixelId || undefined}
              onChange={handleSeriesPixelChange}
              placeholder="请选择像素"
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              notFoundContent="该账户暂无可用像素"
            >
              {accountPixels.map((pixel) => (
                <Option key={pixel.pixel_id} value={pixel.pixel_id}>
                  {pixel.name ? `${pixel.name} (${pixel.pixel_id})` : pixel.pixel_id}
                </Option>
              ))}
            </Select>
          </Col>
          <Col flex="none">
            <span style={{ color: '#999' }}>本系列所有组共用，修改后立即生效</span>
          </Col>
        </Row>
        <Row gutter={12} align="middle">
          <Col flex="none">
            <strong>广告主体：</strong>
          </Col>
          <Col flex="auto" style={{ maxWidth: 480 }}>
            <Select
              allowClear
              value={selectedAdvertiserIdentityId}
              onChange={handleSeriesIdentityChange}
              placeholder="可选；不选则不传区域主体参数"
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              notFoundContent="该 BM 暂无主体"
            >
              {advertiserIdentities.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.display_name}
                  {item.is_default ? '（默认）' : ''}
                  {` · ${item.identity_id}`}
                </Option>
              ))}
            </Select>
          </Col>
          <Col flex="none">
            <span style={{ color: '#999' }}>可选，创建广告组时写入区域合规字段</span>
          </Col>
        </Row>
      </div>
      <Tabs>
        {adsetsData.map((adset, index) => (
          <TabPane tab={<span>组 {index + 1} {adset.status === 'done' && <CheckCircleOutlined style={{ color: 'green' }} />}</span>} key={index}>
            <Form layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="组名称">
                    <Input value={adset.name} disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="显示名称">
                    <Input value={adset.display_name} onChange={(e) => {
                      const newAdsets = [...adsetsData];
                      newAdsets[index].display_name = e.target.value;
                      setAdsetsData(newAdsets);
                    }} />
                  </Form.Item>
                </Col>
              </Row>

              {budgetMode === 'ABO' && (
                <Row gutter={8}>
                  <Col span={6}>
                    <Form.Item label="每日预算" style={{ marginBottom: 8 }}>
                      <Input type="number" value={adset.daily_budget || ''} onChange={(e) => {
                        const newAdsets = [...adsetsData];
                        newAdsets[index].daily_budget = parseFloat(e.target.value) || null;
                        setAdsetsData(newAdsets);
                      }} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="总预算" style={{ marginBottom: 8 }}>
                      <Input type="number" value={adset.lifetime_budget || ''} onChange={(e) => {
                        const newAdsets = [...adsetsData];
                        newAdsets[index].lifetime_budget = parseFloat(e.target.value) || null;
                        setAdsetsData(newAdsets);
                      }} />
                    </Form.Item>
                  </Col>
                  {seriesMode !== SERIES_MODES.ROI && (
                  <Col span={6}>
                    <Form.Item label="出价金额" style={{ marginBottom: 8 }}>
                      <Input type="number" value={adset.bid_amount || ''} onChange={(e) => {
                        const newAdsets = [...adsetsData];
                        newAdsets[index].bid_amount = parseFloat(e.target.value) || null;
                        setAdsetsData(newAdsets);
                      }} />
                    </Form.Item>
                  </Col>
                  )}
                  {seriesMode === SERIES_MODES.ROI && (
                  <Col span={6}>
                    <Form.Item
                      label="ROI系数"
                      required
                      extra="可按组设置不同系数"
                      style={{ marginBottom: 8 }}
                    >
                      <Input
                        type="number"
                        step="0.1"
                        min="0.01"
                        value={adset.roi_coefficient ?? 1.5}
                        onChange={(e) => {
                          const newAdsets = [...adsetsData];
                          const val = parseFloat(e.target.value);
                          newAdsets[index].roi_coefficient = Number.isFinite(val) && val > 0 ? val : 1.5;
                          setAdsetsData(newAdsets);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  )}
                  {seriesMode !== SERIES_MODES.ROI && (
                  <Col span={6}>
                    <Form.Item label="排期" style={{ marginBottom: 8 }}>
                      <RangePicker 
                        showTime 
                        value={adset.schedule} 
                        onChange={(dates) => {
                          const newAdsets = [...adsetsData];
                          newAdsets[index].schedule = dates;
                          setAdsetsData(newAdsets);
                        }} 
                        style={{ width: '100%' }} 
                      />
                    </Form.Item>
                  </Col>
                  )}
                </Row>
              )}

              {budgetMode === 'ABO' && seriesMode === SERIES_MODES.ROI && (
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item label="排期" style={{ marginBottom: 8 }}>
                      <RangePicker 
                        showTime 
                        value={adset.schedule} 
                        onChange={(dates) => {
                          const newAdsets = [...adsetsData];
                          newAdsets[index].schedule = dates;
                          setAdsetsData(newAdsets);
                        }} 
                        style={{ width: '100%' }} 
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {budgetMode === 'CBO' && (
                <Row gutter={8}>
                  {seriesMode !== SERIES_MODES.ROI && (
                  <Col span={8}>
                    <Form.Item
                      label={
                        creationMode === CREATION_MODES.CBO_REGULAR_BID
                          ? (normalizeMetaBidStrategy(campaignData.bid_strategy) === 'COST_CAP'
                            ? '单次成效费用目标 (USD)'
                            : normalizeMetaBidStrategy(campaignData.bid_strategy) === 'LOWEST_COST_WITH_BID_CAP'
                              ? '竞价上限 (USD)'
                              : '出价金额')
                          : '出价金额'
                      }
                      style={{ marginBottom: 8 }}
                    >
                      <Input type="number" value={adset.bid_amount || ''} onChange={(e) => {
                        const newAdsets = [...adsetsData];
                        newAdsets[index].bid_amount = parseFloat(e.target.value) || null;
                        setAdsetsData(newAdsets);
                      }} />
                    </Form.Item>
                  </Col>
                  )}
                  {seriesMode === SERIES_MODES.ROI && (
                  <Col span={8}>
                    <Form.Item label="ROI系数" required extra="可按组设置不同系数" style={{ marginBottom: 8 }}>
                      <Input
                        type="number"
                        step="0.1"
                        min="0.01"
                        value={adset.roi_coefficient ?? 1.5}
                        onChange={(e) => {
                          const newAdsets = [...adsetsData];
                          const val = parseFloat(e.target.value);
                          newAdsets[index].roi_coefficient = Number.isFinite(val) && val > 0 ? val : 1.5;
                          setAdsetsData(newAdsets);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  )}
                  <Col span={8}>
                    <Form.Item label="排期" style={{ marginBottom: 8 }}>
                      <RangePicker 
                        showTime 
                        value={adset.schedule} 
                        onChange={(dates) => {
                          const newAdsets = [...adsetsData];
                          newAdsets[index].schedule = dates;
                          setAdsetsData(newAdsets);
                        }} 
                        style={{ width: '100%' }} 
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={8}>
                <Col span={6}>
                  <Form.Item label="优化目标" style={{ marginBottom: 8 }}>
                    <Select value={adset.optimization_goal} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      newAdsets[index].optimization_goal = value;
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="LINK_CLICKS">链接点击</Option>
                      <Option value="IMPRESSIONS">展示</Option>
                      <Option value="REACH">覆盖人数</Option>
                      <Option value="OFFSITE_CONVERSIONS">转化</Option>
                      <Option value="LEAD_GENERATION">潜在客户</Option>
                      <Option value="APP_INSTALLS">应用安装</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="计费事件" style={{ marginBottom: 8 }}>
                    <Select value={adset.billing_event} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      newAdsets[index].billing_event = value;
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="IMPRESSIONS">展示</Option>
                      <Option value="LINK_CLICKS">链接点击</Option>
                      <Option value="APP_INSTALLS">应用安装</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="出价策略" style={{ marginBottom: 8 }}>
                    <Select value={adset.bid_strategy} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      newAdsets[index].bid_strategy = value;
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="LOWEST_COST_WITHOUT_CAP">最低成本无上限</Option>
                      <Option value="COST_CAP">成本上限</Option>
                      <Option value="LOWEST_COST_WITH_BID_CAP">最低成本带出价上限</Option>
                      <Option value="LOWEST_COST_WITH_MIN_ROAS">广告花费回报目标</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="归因设置" style={{ marginBottom: 8 }}>
                    <Select value={adset.attribution_setting || '7D_CLICK_1D_VIEW'} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      newAdsets[index].attribution_setting = value;
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="7D_CLICK_1D_VIEW">7天点击/1天浏览</Option>
                      <Option value="1D_CLICK_1D_VIEW">1天点击/1天浏览</Option>
                      <Option value="7D_CLICK">仅7天点击</Option>
                      <Option value="1D_CLICK">仅1天点击</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" style={{ margin: '12px 0' }}>广告格式设置</Divider>
              
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item label="动态素材广告" style={{ marginBottom: 8 }}>
                    <Select value={adset.use_dynamic_creative ? 'true' : 'false'} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      newAdsets[index].use_dynamic_creative = value === 'true';
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="false">标准广告（单版本）</Option>
                      <Option value="true">动态素材广告（多版本）</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  {adset.use_dynamic_creative && (
                    <div style={{ padding: '8px 12px', background: '#e6f7ff', borderRadius: 4, marginTop: 29 }}>
                      <span style={{ color: '#1890ff' }}>提示：启用后可在步骤3中添加多个文案/标题/描述</span>
                    </div>
                  )}
                </Col>
              </Row>

              <Divider orientation="left" style={{ margin: '12px 0' }}>转化设置</Divider>
              
              <Row gutter={8}>
                <Col span={6}>
                  <Form.Item label="转化位置" style={{ marginBottom: 8 }}>
                    <Select value={adset.conversion_location || 'WEBSITE'} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      newAdsets[index].conversion_location = value;
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="WEBSITE">网站</Option>
                      <Option value="APP">应用</Option>
                      <Option value="MESSENGER">Messenger</Option>
                      <Option value="WHATSAPP">WhatsApp</Option>
                      <Option value="ON_AD">广告上</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="成效目标" style={{ marginBottom: 8 }}>
                    <Select value={adset.performance_goal || 'MAXIMIZE_CONVERSIONS'} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      newAdsets[index].performance_goal = value;
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="MAXIMIZE_CONVERSIONS">转化量最大化</Option>
                      {!(
                        creationMode === CREATION_MODES.CBO_REGULAR_BID
                        && ['LOWEST_COST_WITH_BID_CAP', 'COST_CAP'].includes(
                          normalizeMetaBidStrategy(campaignData.bid_strategy, 'LOWEST_COST_WITH_BID_CAP')
                        )
                      ) && (
                        <Option value="MAXIMIZE_CONVERSION_VALUE">转化价值最大化</Option>
                      )}
                      <Option value="MAXIMIZE_LINK_CLICKS">链接点击最大化</Option>
                      <Option value="MAXIMIZE_IMPRESSIONS">展示最大化</Option>
                      <Option value="MAXIMIZE_REACH">覆盖人数最大化</Option>
                      <Option value="MAXIMIZE_THRUPLAY">完整观看最大化</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Pixel ID" style={{ marginBottom: 8 }} extra="系列共用，可在顶部切换">
                    <Input value={selectedPixelId || adset.pixel_id || ''} disabled />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="转化事件" style={{ marginBottom: 8 }}>
                    <Select value={adset.conversion_event || 'PURCHASE'} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      newAdsets[index].conversion_event = value;
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="PURCHASE">购买</Option>
                      <Option value="ADD_TO_CART">加购</Option>
                      <Option value="INITIATE_CHECKOUT">发起结账</Option>
                      <Option value="LEAD">潜在客户</Option>
                      <Option value="COMPLETE_REGISTRATION">完成注册</Option>
                      <Option value="VIEW_CONTENT">查看内容</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" style={{ margin: '12px 0' }}>定向设置</Divider>
              
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item label="国家" style={{ marginBottom: 8 }}>
                    <Select mode="multiple" value={adset.targeting?.geo_locations?.countries || ['US']} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      if (!newAdsets[index].targeting) newAdsets[index].targeting = {};
                      if (!newAdsets[index].targeting.geo_locations) newAdsets[index].targeting.geo_locations = {};
                      newAdsets[index].targeting.geo_locations.countries = value;
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="US">美国</Option>
                      <Option value="CA">加拿大</Option>
                      <Option value="GB">英国</Option>
                      <Option value="AU">澳大利亚</Option>
                      <Option value="DE">德国</Option>
                      <Option value="FR">法国</Option>
                      <Option value="JP">日本</Option>
                      <Option value="KR">韩国</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="语言" style={{ marginBottom: 8 }}>
                    <Select 
                      mode="multiple" 
                      value={adset.targeting?.locales || []} 
                      onChange={(value) => {
                        const newAdsets = [...adsetsData];
                        if (!newAdsets[index].targeting) newAdsets[index].targeting = {};
                        newAdsets[index].targeting.locales = value;
                        setAdsetsData(newAdsets);
                      }}
                      labelInValue
                      options={[
                        { value: 'zh', label: '中文' },
                        { value: 'en', label: '英语' },
                        { value: 'ja', label: '日语' },
                        { value: 'ko', label: '韩语' },
                        { value: 'fr', label: '法语' },
                        { value: 'de', label: '德语' },
                        { value: 'es', label: '西班牙语' },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="投放平台" style={{ marginBottom: 8 }}>
                    <Select mode="multiple" value={adset.targeting?.publisher_platforms || ['facebook', 'instagram']} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      if (!newAdsets[index].targeting) newAdsets[index].targeting = {};
                      newAdsets[index].targeting.publisher_platforms = value;
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="facebook">Facebook</Option>
                      <Option value="instagram">Instagram</Option>
                      <Option value="audience_network">Audience Network</Option>
                      <Option value="messenger">Messenger</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={6}>
                  <Form.Item label="年龄最小" style={{ marginBottom: 8 }}>
                    <Input type="number" min={13} max={65} value={adset.targeting?.age_min || 18} onChange={(e) => {
                      const newAdsets = [...adsetsData];
                      if (!newAdsets[index].targeting) newAdsets[index].targeting = {};
                      newAdsets[index].targeting.age_min = parseInt(e.target.value) || 18;
                      setAdsetsData(newAdsets);
                    }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="年龄最大" style={{ marginBottom: 8 }}>
                    <Input type="number" min={13} max={65} value={adset.targeting?.age_max || 65} onChange={(e) => {
                      const newAdsets = [...adsetsData];
                      if (!newAdsets[index].targeting) newAdsets[index].targeting = {};
                      newAdsets[index].targeting.age_max = parseInt(e.target.value) || 65;
                      setAdsetsData(newAdsets);
                    }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="性别" style={{ marginBottom: 8 }}>
                    <Select value={JSON.stringify(adset.targeting?.genders || [0, 1])} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      if (!newAdsets[index].targeting) newAdsets[index].targeting = {};
                      newAdsets[index].targeting.genders = JSON.parse(value);
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="[0,1]">全部</Option>
                      <Option value="[1]">男性</Option>
                      <Option value="[0]">女性</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="赋能受众" style={{ marginBottom: 8 }}>
                    <Select value={adset.advantage_audience ?? 0} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      newAdsets[index].advantage_audience = value;
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value={0}>关闭</Option>
                      <Option value={1}>开启</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item label="设备" style={{ marginBottom: 8 }}>
                    <Select mode="multiple" value={adset.device_targeting?.devices || ['mobile', 'desktop']} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      if (!newAdsets[index].device_targeting) newAdsets[index].device_targeting = {};
                      newAdsets[index].device_targeting.devices = value;
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="mobile">手机</Option>
                      <Option value="desktop">桌面电脑</Option>
                      <Option value="tablet">平板</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="操作系统" style={{ marginBottom: 8 }}>
                    <Select mode="multiple" value={adset.device_targeting?.os || []} onChange={(value) => {
                      const newAdsets = [...adsetsData];
                      if (!newAdsets[index].device_targeting) newAdsets[index].device_targeting = {};
                      newAdsets[index].device_targeting.os = value;
                      setAdsetsData(newAdsets);
                    }}>
                      <Option value="iOS">iOS</Option>
                      <Option value="Android">Android</Option>
                      <Option value="Windows">Windows</Option>
                      <Option value="Mac">Mac OS</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Button type="primary" onClick={() => handleSaveAdset(index)} loading={loading} disabled={adset.status === 'done'}>
                {adset.status === 'done' ? '已创建' : '保存并创建'}
              </Button>
            </Form>
          </TabPane>
        ))}
      </Tabs>

      <Divider />
      <Space>
        <Button onClick={() => setCurrentStep(1)}>上一步</Button>
        <Button type="primary" onClick={handleSaveAllAdsets} loading={loading}>批量创建所有AdSet</Button>
        <Button type="primary" onClick={handleCompleteAdsets} loading={loading}>完成所有组并下一步</Button>
      </Space>
    </Card>
    );
  };

  // 步骤3进入时自动匹配和上传图片（仅首次进入）
  useEffect(() => {
    if (currentStep !== 3) {
      step3MaterialsMatchRef.current = false;
      return;
    }
    if (materialsUploaded || step3MaterialsMatchRef.current) {
      return;
    }
    step3MaterialsMatchRef.current = true;

    const matchAndUploadMaterials = async () => {
        message.loading('正在匹配素材...', 0);
        const materialsResult = await matchAllAdMaterials();
        message.destroy();
        
        if (materialsResult) {
          if (materialsResult.need_upload.length > 0) {
            // 需要上传素材
            message.loading('正在上传素材...', 0);
            await batchUploadMaterials(materialsResult.need_upload);
            message.destroy();
          } else {
            // 所有素材都已上传
            setMaterialsUploaded(true);
            message.success('所有素材已上传，可进行广告创建');
          }
        }
      };
      
      matchAndUploadMaterials();
  }, [currentStep, materialsUploaded]);
  
  // 步骤3进入时自动查询文案数据（仅首次进入）
  useEffect(() => {
    if (currentStep !== 3 || !taskId) {
      if (currentStep !== 3) {
        step3CopyLoadedRef.current = false;
      }
      return;
    }
    if (step3CopyLoadedRef.current) {
      return;
    }
    step3CopyLoadedRef.current = true;

    const handleEnterStep3 = async () => {
        try {
          // 查询关联的单个文案数据
          const response = await api.get(`/ad-creation-tasks/${taskId}/creative-copy`);
          const creativeCopy = response.data;
          
          // 如果有文案数据，为每个广告分配随机搭配的文案
          if (creativeCopy && (creativeCopy.bodies.length > 0 || creativeCopy.headlines.length > 0 || creativeCopy.descriptions.length > 0)) {
            const newAdsData = [...adsData];
            
            newAdsData.forEach(group => {
              group.forEach(ad => {
                // 广告已创建成功，不修改文案
                if (ad.status === 'done') {
                  return;
                }
                
                // 获取广告配置的文案数量（从模板继承）
                const bodyCount = ad.params?.body_count || 5;
                const headlineCount = ad.params?.headline_count || 5;
                const descriptionCount = ad.params?.description_count || 5;
                
                // 为当前广告随机选择文案、标题和描述的组合，确保同一个广告内不重复
                if (creativeCopy.bodies.length > 0) {
                  const selectedBodies = [];
                  const availableBodies = [...creativeCopy.bodies];
                  
                  for (let i = 0; i < bodyCount && availableBodies.length > 0; i++) {
                    const randomIndex = Math.floor(Math.random() * availableBodies.length);
                    selectedBodies.push(availableBodies[randomIndex]);
                    availableBodies.splice(randomIndex, 1); // 从可用列表中移除，避免重复
                  }
                  
                  ad.messages = selectedBodies;
                  ad.message = selectedBodies[0] || '';
                }
                
                if (creativeCopy.headlines.length > 0) {
                  const selectedHeadlines = [];
                  const availableHeadlines = [...creativeCopy.headlines];
                  
                  for (let i = 0; i < headlineCount && availableHeadlines.length > 0; i++) {
                    const randomIndex = Math.floor(Math.random() * availableHeadlines.length);
                    selectedHeadlines.push(availableHeadlines[randomIndex]);
                    availableHeadlines.splice(randomIndex, 1); // 从可用列表中移除，避免重复
                  }
                  
                  ad.headlines = selectedHeadlines;
                  ad.headline = selectedHeadlines[0] || '';
                }
                
                if (creativeCopy.descriptions.length > 0) {
                  const selectedDescriptions = [];
                  const availableDescriptions = [...creativeCopy.descriptions];
                  
                  for (let i = 0; i < descriptionCount && availableDescriptions.length > 0; i++) {
                    const randomIndex = Math.floor(Math.random() * availableDescriptions.length);
                    selectedDescriptions.push(availableDescriptions[randomIndex]);
                    availableDescriptions.splice(randomIndex, 1); // 从可用列表中移除，避免重复
                  }
                  
                  ad.descriptions = selectedDescriptions;
                }
              });
            });
            
            setAdsData(newAdsData);
            message.success('文案匹配和随机分配完成');
          }
        } catch (error) {
          console.error('获取文案数据失败:', error);
          message.error('获取文案数据失败');
        }
      };
      
      handleEnterStep3();
  }, [currentStep, taskId]);
  
  const hasUnlockedAds = adsData.some((group) =>
    group.some((ad) => !isAdPageLocked(ad))
  );
  const showPageActions =
    !loading && accountPages.length === 0 && hasUnlockedAds && selectedAccount;
  const manualPageDisplay =
    manualPageId &&
    (accountPages[0] ||
      developerPages.find((p) => String(p.page_id) === String(manualPageId)));

  const renderStep3 = () => (
    <Card title="步骤 3/3: 创建广告">
      {task?.developer_account_name && (
        <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
          公共主页列表已与当前开发者「{task.developer_account_name}」关联主页取交集，避免无权限主页。
        </div>
      )}

      {showPageActions && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: '#999', marginBottom: 8 }}>
            该广告账户尚未加载可推广公共主页。请先同步当前账户（写入本地数据库），或从开发者已关联的主页中手动选择。
          </div>
          <Space wrap>
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={() => handleSyncAccountPages()}
              loading={syncingAccountPages}
            >
              同步该账户可推广主页
            </Button>
            <Button
              onClick={handleOpenManualPageModal}
              loading={fetchingDeveloperPages}
            >
              手动选择公共主页
            </Button>
          </Space>
          {manualPageDisplay && (
            <div style={{ marginTop: 8, color: '#52c41a' }}>
              已选择：{formatPageLabel(manualPageDisplay)}（将应用于全部待创建广告）
            </div>
          )}
        </div>
      )}

      <Modal
        title="手动选择公共主页"
        open={manualPageModalOpen}
        onCancel={() => setManualPageModalOpen(false)}
        onOk={handleConfirmManualPage}
        okText="确认并应用到全部广告"
        cancelText="取消"
        confirmLoading={fetchingDeveloperPages}
        destroyOnClose
      >
        <p style={{ color: '#666', marginBottom: 12 }}>
          以下为主页与当前任务开发者关联主页的交集；若为空请先同步账户可推广主页，并在「开发者管理」分配主页权限。
        </p>
        <Select
          showSearch
          placeholder="选择公共主页"
          style={{ width: '100%' }}
          loading={fetchingDeveloperPages}
          value={manualPagePick}
          optionFilterProp="label"
          onChange={(value) => setManualPagePick(value)}
          options={(developerPages || []).map((page) => {
            const pid = String(page.page_id || page.id);
            const selectable = isPageSelectable(page);
            return {
              value: pid,
              label: formatPageLabel(page) + (selectable ? '' : '（不可用）'),
              disabled: !selectable,
            };
          })}
        />
      </Modal>

      <Tabs>
        {adsData.map((group, groupIndex) => {
          // 检查该组所有广告是否都已创建成功
          const allAdsDone = group.every(ad => ad.status === 'done');
          return (
            <TabPane 
              tab={
                <span>
                  组 {groupIndex + 1} 
                  {allAdsDone && <CheckCircleOutlined style={{ color: 'green', marginLeft: 4 }} />}
                </span>
              } 
              key={groupIndex}
            >
              <Tabs>
                {group.map((ad, adIndex) => {
                  return (
                    <TabPane 
                      tab={<span>广告 {adIndex + 1} {ad.status === 'done' && <CheckCircleOutlined style={{ color: 'green' }} />}</span>} 
                      key={adIndex}
                    >
                      <Form layout="vertical">

                     <Form.Item label="Facebook公共主页">
                      {(loadingPages || syncingAccountPages) ? (
                        <div style={{ padding: 8, color: '#999' }}>加载主页中...</div>
                      ) : isAdPageLocked(ad) ? (
                        <Input
                          value={ad.page_id ? String(ad.page_id) : ''}
                          disabled
                          addonBefore="已创建"
                        />
                      ) : accountPages.length > 0 ? (
                        <Select
                          showSearch
                          placeholder="选择可分配的公共主页"
                          value={
                            ad.page_id != null &&
                            ad.page_id !== '' &&
                            accountPages.some((p) => String(p.page_id) === String(ad.page_id))
                              ? String(ad.page_id)
                              : undefined
                          }
                          optionFilterProp="label"
                          onChange={(value) => {
                            const newAds = [...adsData];
                            newAds[groupIndex][adIndex].page_id = value ? String(value) : null;
                            setAdsData(newAds);
                          }}
                          style={{ width: '100%' }}
                          options={accountPages.filter(isPageSelectable).map((page) => {
                            const pid = String(page.page_id || page.id);
                            return {
                              value: pid,
                              label: formatPageLabel(page),
                            };
                          })}
                        />
                      ) : manualPageId ? (
                        <Input
                          value={
                            manualPageDisplay
                              ? formatPageLabel(manualPageDisplay)
                              : String(manualPageId)
                          }
                          disabled
                        />
                      ) : (
                        <div style={{ color: '#999' }}>
                          请点击上方「同步该账户可推广主页」或「手动选择公共主页」
                        </div>
                      )}
                    </Form.Item>
                    <Form.Item label="广告名称">
                      <Input value={ad.name} disabled />
                    </Form.Item>

                    <Form.Item label="显示名称">
                      <Input value={ad.display_name} onChange={(e) => {
                        const newAds = [...adsData];
                        newAds[groupIndex][adIndex].display_name = e.target.value;
                        setAdsData(newAds);
                      }} />
                    </Form.Item>

                    <Form.Item label="广告文案">
                      {((ad.messages || [ad.message || '']) || []).map((msg, msgIndex) => (
                        <div key={msgIndex} style={{ display: 'flex', marginBottom: 8 }}>
                          <TextArea 
                            rows={2} 
                            value={msg} 
                            onChange={(e) => {
                              const newAds = [...adsData];
                              if (!newAds[groupIndex][adIndex].messages) {
                                newAds[groupIndex][adIndex].messages = [ad.message || ''];
                              }
                              newAds[groupIndex][adIndex].messages[msgIndex] = e.target.value;
                              // 同步更新主 message 字段（兼容旧逻辑）
                              newAds[groupIndex][adIndex].message = newAds[groupIndex][adIndex].messages[0];
                              setAdsData(newAds);
                            }} 
                            placeholder={`广告文案 ${msgIndex + 1}`}
                            style={{ flex: 1 }}
                          />
                          {msgIndex > 0 && (
                            <Button 
                              danger 
                              onClick={() => {
                                const newAds = [...adsData];
                                newAds[groupIndex][adIndex].messages.splice(msgIndex, 1);
                                newAds[groupIndex][adIndex].message = newAds[groupIndex][adIndex].messages[0] || '';
                                setAdsData(newAds);
                              }}
                              style={{ marginLeft: 8 }}
                            >
                              删除
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button 
                        type="dashed" 
                        onClick={() => {
                          const newAds = [...adsData];
                          if (!newAds[groupIndex][adIndex].messages) {
                            newAds[groupIndex][adIndex].messages = [ad.message || ''];
                          }
                          newAds[groupIndex][adIndex].messages.push('');
                          setAdsData(newAds);
                        }}
                        style={{ width: '100%' }}
                      >
                        + 添加文案
                      </Button>
                    </Form.Item>

                    <Form.Item label="标题">
                      {((ad.headlines || [ad.headline || '']) || []).map((hdl, hdlIndex) => (
                        <div key={hdlIndex} style={{ display: 'flex', marginBottom: 8 }}>
                          <Input 
                            value={hdl} 
                            onChange={(e) => {
                              const newAds = [...adsData];
                              if (!newAds[groupIndex][adIndex].headlines) {
                                newAds[groupIndex][adIndex].headlines = [ad.headline || ''];
                              }
                              newAds[groupIndex][adIndex].headlines[hdlIndex] = e.target.value;
                              // 同步更新主 headline 字段（兼容旧逻辑）
                              newAds[groupIndex][adIndex].headline = newAds[groupIndex][adIndex].headlines[0];
                              setAdsData(newAds);
                            }} 
                            placeholder={`标题 ${hdlIndex + 1}`}
                            style={{ flex: 1 }}
                          />
                          {hdlIndex > 0 && (
                            <Button 
                              danger 
                              onClick={() => {
                                const newAds = [...adsData];
                                newAds[groupIndex][adIndex].headlines.splice(hdlIndex, 1);
                                newAds[groupIndex][adIndex].headline = newAds[groupIndex][adIndex].headlines[0] || '';
                                setAdsData(newAds);
                              }}
                              style={{ marginLeft: 8 }}
                            >
                              删除
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button 
                        type="dashed" 
                        onClick={() => {
                          const newAds = [...adsData];
                          if (!newAds[groupIndex][adIndex].headlines) {
                            newAds[groupIndex][adIndex].headlines = [ad.headline || ''];
                          }
                          newAds[groupIndex][adIndex].headlines.push('');
                          setAdsData(newAds);
                        }}
                        style={{ width: '100%' }}
                      >
                        + 添加标题
                      </Button>
                    </Form.Item>

                    <Form.Item label="描述">
                      {((ad.descriptions || [ad.description || '']) || []).map((desc, descIndex) => (
                        <div key={descIndex} style={{ display: 'flex', marginBottom: 8 }}>
                          <TextArea 
                            rows={2} 
                            value={desc} 
                            onChange={(e) => {
                              const newAds = [...adsData];
                              if (!newAds[groupIndex][adIndex].descriptions) {
                                newAds[groupIndex][adIndex].descriptions = [ad.description || ''];
                              }
                              newAds[groupIndex][adIndex].descriptions[descIndex] = e.target.value;
                              setAdsData(newAds);
                            }} 
                            placeholder={`描述 ${descIndex + 1}`}
                            style={{ flex: 1 }}
                          />
                          {descIndex > 0 && (
                            <Button 
                              danger 
                              onClick={() => {
                                const newAds = [...adsData];
                                newAds[groupIndex][adIndex].descriptions.splice(descIndex, 1);
                                setAdsData(newAds);
                              }}
                              style={{ marginLeft: 8 }}
                            >
                              删除
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button 
                        type="dashed" 
                        onClick={() => {
                          const newAds = [...adsData];
                          if (!newAds[groupIndex][adIndex].descriptions) {
                            newAds[groupIndex][adIndex].descriptions = [ad.description || ''];
                          }
                          newAds[groupIndex][adIndex].descriptions.push('');
                          setAdsData(newAds);
                        }}
                        style={{ width: '100%' }}
                      >
                        + 添加描述
                      </Button>
                    </Form.Item>

                   

                    <Form.Item label="行动号召">
                      <Select value={ad.call_to_action} onChange={(value) => {
                        const newAds = [...adsData];
                        newAds[groupIndex][adIndex].call_to_action = value;
                        setAdsData(newAds);
                      }}>
                        <Option value="SHOP_NOW">立即购买</Option>
                        <Option value="LEARN_MORE">了解更多</Option>
                        <Option value="SEE_DETAILS">查看详情</Option>
                        <Option value="SIGN_UP">注册</Option>
                        <Option value="DOWNLOAD">下载</Option>
                        <Option value="BOOK_TRAVEL">预订行程</Option>
                        <Option value="CONTACT_US">联系我们</Option>
                        <Option value="GET_QUOTE">获取报价</Option>
                        <Option value="GET_OFFER">获取优惠</Option>
                        <Option value="GET_DIRECTIONS">获取路线</Option>
                        <Option value="SUBSCRIBE">订阅</Option>
                        <Option value="WATCH_MORE">观看更多</Option>
                        <Option value="APPLY_NOW">立即申请</Option>
                        <Option value="BUY_NOW">立即购买</Option>
                        <Option value="ORDER_NOW">立即订购</Option>
                        <Option value="DONATE_NOW">立即捐赠</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item label="链接 URL">
                      <Input value={ad.link_url} onChange={(e) => {
                        const newAds = [...adsData];
                        newAds[groupIndex][adIndex].link_url = e.target.value;
                        setAdsData(newAds);
                      }} placeholder="https://..." />
                    </Form.Item>

                    <Form.Item label="素材">
                      <Select
                        mode="multiple"
                        placeholder="选择素材"
                        value={ad.material_ids || []}
                        onChange={(value) => {
                          const newAds = [...adsData];
                          newAds[groupIndex][adIndex].material_ids = value;
                          setAdsData(newAds);
                        }}
                        style={{ width: '100%' }}
                      >
                        {((selectedMaterialCollection ? collectionMaterials : materials) || []).map(material => (
                          <Option key={material.id} value={material.id}>
                            {material.name || material.filename || `素材 ${material.id}`}
                          </Option>
                        ))}
                      </Select>
                      {selectedMaterialCollection && (
                        <div style={{ marginTop: 8, color: '#1890ff', fontSize: 12 }}>
                          <PictureOutlined style={{ marginRight: 4 }} />
                          当前使用素材集: {selectedMaterialCollection.name} ({collectionMaterials.length}个素材)
                        </div>
                      )}
                      {/* 显示选中的素材预览 */}
                      {(ad.material_ids && ad.material_ids.length > 0) && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>已选择的素材:</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {(ad.material_ids || []).map(materialId => {
                              const material = (selectedMaterialCollection ? collectionMaterials : materials).find(m => m.id === materialId);
                              if (material && material.file_url) {
                                const isVideo = (material.mime_type || '').startsWith('video/')
                                  || /\.(mp4|mov|avi|webm|mkv|m4v)$/i.test(material.file_url || material.name || '');
                                return (
                                  <div key={materialId} style={{ position: 'relative' }}>
                                    {isVideo ? (
                                      <video
                                        src={getImageUrl(material.file_url)}
                                        style={{ width: 120, height: 120, objectFit: 'cover', border: '1px solid #e8e8e8', borderRadius: 4, background: '#111' }}
                                        muted
                                        playsInline
                                      />
                                    ) : (
                                      <img
                                        src={getImageUrl(material.file_url)}
                                        alt={material.name || `素材 ${materialId}`}
                                        style={{ width: 120, height: 120, objectFit: 'cover', border: '1px solid #e8e8e8', borderRadius: 4 }}
                                      />
                                    )}
                                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: 'white', padding: 4, fontSize: 10, textAlign: 'center' }}>
                                      {isVideo ? '[视频] ' : ''}{material.name || `素材 ${materialId}`}
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })}
                          </div>
                        </div>
                      )}
                    </Form.Item>

                    {/* 显示广告创建失败信息 */}
                    {errorInfo.some(err => err.adset_index === groupIndex && err.ad_index === adIndex) && (
                      <div style={{ marginTop: 8 }}>
                        {errorInfo
                          .filter(err => err.adset_index === groupIndex && err.ad_index === adIndex)
                          .map((err, idx) => (
                            <div 
                              key={idx} 
                              style={{ 
                                color: 'red', 
                                fontSize: '12px', 
                                background: '#ffe6e6', 
                                padding: '4px 8px', 
                                borderRadius: '4px',
                                marginBottom: '4px'
                              }}
                            >
                              <span style={{ fontWeight: 'bold' }}>创建失败: </span>
                              {err.error}
                            </div>
                          ))}
                      </div>
                    )}
                    
                    <Button type="primary" onClick={() => handleSaveAd(groupIndex, adIndex)} loading={loading} disabled={ad.status === 'done'}>
                      {ad.status === 'done' ? '已创建' : '保存并创建'}
                    </Button>
                        </Form>
                      </TabPane>
                    );
                  })}
              </Tabs>
            </TabPane>
          );
        })}
      </Tabs>

      <Divider />
      <Space>
        <Button onClick={async () => {
          setCurrentStep(2);
          // 返回到步骤2时，需要重新获取广告账户的像素信息
          if (selectedAccount) {
            console.log('Returning to step 2, fetching account pixels...');
            await fetchAccountPixels(selectedAccount);
          }
        }}>上一步</Button>
        <Button 
          type="primary" 
          onClick={handleSaveAllAds} 
          loading={loading || matchingMaterials || uploadingMaterials}
          disabled={!materialsUploaded && !hasAdMaterialsHash()}
        >
          {matchingMaterials ? '匹配素材中...' : uploadingMaterials ? '上传素材中...' : '批量创建所有广告'}
        </Button>
        <Button
          type="primary"
          onClick={handleCompleteAds}
          loading={loading}
          disabled={!areAllAdsCreated()}
        >
          完成
        </Button>
      </Space>
      
      {/* 添加上传进度条 */}
      {uploadingMaterials && (
        <div style={{ marginTop: 16 }}>
          <Progress percent={materialUploadProgress} status="active" />
        </div>
      )}
    </Card>
  );

  return (
    <div style={{ padding: 24 }}>
      <h2><RocketOutlined /> 创建广告</h2>
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        <Step title="选择模板" icon={<SettingOutlined />} />
        <Step title="Campaign" icon={<RocketOutlined />} />
        <Step title="AdSets" icon={<SettingOutlined />} />
        <Step title="广告" icon={<PictureOutlined />} />
      </Steps>

      {currentStep === 0 && renderStep0()}
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}

      <Modal
        title="账户信息未就绪"
        open={accountPrereqModal.open}
        onCancel={() => setAccountPrereqModal({ open: false, issues: [] })}
        footer={[
          <Button
            key="cancel"
            onClick={() => setAccountPrereqModal({ open: false, issues: [] })}
          >
            取消
          </Button>,
          <Button
            key="sync"
            type="primary"
            icon={<SyncOutlined />}
            loading={syncingAccountPages}
            onClick={handleAccountPrereqSync}
          >
            同步账户信息
          </Button>,
        ]}
      >
        <p>创建系列前，需要该广告账户已同步以下信息：</p>
        <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
          {accountPrereqModal.issues.map((issue, index) => (
            <li key={index} style={{ marginBottom: 4 }}>{issue}</li>
          ))}
        </ul>
        <p style={{ color: '#666', marginBottom: 0 }}>
          点击「同步账户信息」将从 Facebook 拉取可推广公共主页和像素到本地。同步成功且数据齐全后将自动继续创建任务。
        </p>
      </Modal>
    </div>
  );
};

export default AdCreation;
